import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { isEmpty, cloneDeep, round, set, get } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { AddIcon, EditIcon, SaveIcon, ChevronIcon, InfoIcon } from 'bombiix-icon';
import BombiixAGGrid from '../../Components/BombiixAGGrid';
import type { BombiixColumnDef, BombiixCellRendererParams, BombiixRowId, BombiixFillBatchItem } from '../../Components/BombiixAGGrid';
import { PORTAL_ID as DATEPICKER_PORTAL_ID } from '../../Components/BombiixAGGrid/cells/DateInputCell';
import { NewInput, NewSelectMultiSelect, NewCheckbox, NewRadio, NewDatePicker } from '../../Components/PIM/common';
import Loader from '../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import { localStorageService } from '../../services/localStorage.service';
import { checkArray, currencySymbols, ERROR_MESSAGE, shortenandHoverString, getCurrentYearLabel } from '../../Components/helper';
import { usePlaceholderState, PlaceholderRow, PlaceholderDynamicColumn, PlaceholderMultiDynamicColumn, LabelOption } from './context/PlaceholderProvider';
import {
  placeholderManagementPath,
  dynamicColumnPath,
  productListPath,
  productListDynamicPath,
  multiDynamicColumnPath,
  productListMultiDynamicColumnPath,
} from './fieldPath';
import { IdNameOption, MultiDynamicColumnMaster } from './types';
import {
  getIndividualDetails,
  getPlaceholderData,
  deletePlaceholderData,
  clearReducer,
  updateQty as updateQtyAction,
  fetchPlaceholderProduct,
  savePlaceholderProductAction,
} from './store/placeholder.actions';
import { getCatBasedOnPT, getCustomAttributeFreeText, getTypeBasedCatgeory } from '../SystemMasters/Store/SystemMasters.action';
import { getCounterValueNumber } from '../DevlopmentOrder/store/DevelopmentOrder.action';
import ReplacePlaceholderModal from './ReplacePlaceholderModal';
import { PLACEHOLDER_ROUTES } from './placeholder.constants';
import {
  acceptPlaceholderQuantity,
  applyChildFill,
  applyMasterFill,
  idOf,
  isMultiDynamicEnabledForRow,
  MASTER_SELECT_FILL_ORDER,
  upsertMultiDynamicEntry,
  matchOption,
  multiDynamicIdsOf,
  gridIdOf,
  masterFillRank,
  withReloadedAttributes,
} from './placeholder-fill';
import type { AttributeReload, MasterFillContext } from './placeholder-fill';

export interface PlaceholderGridProps {
  handleSelection: (value: string | boolean) => void;
  mainState: { isSelectAll?: boolean; selectedIdsArray: string[] };
  placeholderAddEditAccess?: boolean;
  placeholderDeleteAccess?: boolean;
  refreshFunction?: () => void;
  subBrandAccess?: boolean;
}

interface CategoryMasterOption extends LabelOption {
  categoryOneId?: string;
  categoryTwoId?: string;
  categoryThreeId?: string;
  isActive?: boolean;
  productType?: Array<{ _id?: string } | string> | { _id?: string } | string;
  isUsedCat1?: Array<{ _id?: string } | string>;
  catOneIds?: Array<{ _id?: string } | string> | { _id?: string } | string;
  catTwoIds?: Array<{ _id?: string } | string> | { _id?: string } | string;
  catThreeIds?: Array<{ _id?: string } | string> | { _id?: string } | string;
}

/**
 * Master-row fields edited through plain text inputs; these commit per keystroke
 * via `handleRowChange` rather than the async select-driven `handleChange`.
 */
const FREE_TEXT_ROW_FIELDS = ['productDesc', 'placeholderQuantity'];

interface PMGridRow extends PlaceholderRow {
  __gridId?: string;
  __isChild?: boolean;
  __parentIndex?: number;
  __childIndex?: number;
  __selected?: boolean;
  catOneName?: string;
  catTwoName?: string;
  catThreeName?: string;
  catFourName?: string;
  number?: string;
  productListClone?: unknown[];
  [key: string]: unknown;
}

interface DynamicMeta {
  _id: string;
  keyName: string;
  attrType: string;
  options: Array<{ _id: string; keyName?: string; isActive?: boolean }>;
}

interface MultiDynamicMeta {
  _id: string;
  keyName: string;
  options: Array<{ _id: string; keyName?: string; isActive?: boolean }>;
  productType: string[];
}

type DynEle = PlaceholderDynamicColumn & {
  valueText?: string;
  isChecked?: boolean;
  productType?: string[];
  brand?: string[];
  datePickerConfig?: { isEditable?: boolean; dateFormat?: string; isRequired?: boolean };
  textFieldConfig?: { isExceedable?: boolean; charLimit?: number; isHighlightable?: boolean; isEditable?: boolean };
};

type ChangeFn = (val: unknown, path: string, i?: number) => Promise<void>;

interface ConfirmationPopupProps {
  onOpen: boolean;
  onConfirm: () => void;
  title: React.ReactNode;
  message: React.ReactNode;
  toggle: () => void;
  loading?: boolean;
}

const ConfirmationPopupTyped = ConfirmationPopup as unknown as React.FC<ConfirmationPopupProps>;

interface CellCtx {
  placeholderManagement: PMGridRow[];
  productTypeOptions: IdNameOption[];
  category1Options: CategoryMasterOption[];
  category2Options: CategoryMasterOption[];
  category3Options: CategoryMasterOption[];
  category4Options: CategoryMasterOption[];
  brandOptions: IdNameOption[];
  subBrandOptions: Array<IdNameOption & { brandId?: string }>;
  seasonOptions: IdNameOption[];
  monthOptions: IdNameOption[];
  yearOptions: IdNameOption[];
  catBasedOnPTData: boolean;
  subBrandAccess: boolean;
  addEditAccess: boolean;
  deleteAccess: boolean;
  isArchived: boolean;
  currencySymbol: string;
  globalError: Record<string, Record<string, string>>;
  placeholderList: Array<{ _id?: string; placeholderQuantity?: string | number }>;
  duplicateExist: number[];
  mainState: { isSelectAll?: boolean; selectedIdsArray: string[] };
  handleSelection: (value: string | boolean) => void;
  handleChange: ChangeFn;
  handleBrandChange: (val: unknown, i: number) => void;
  handleDynamicChange: (path: { keyName: string; value: unknown }[], i: number, dyIndex: number) => void;
  handleMultiDynamicChange: (colId: string, valIds: string[], i: number) => void;
  multiDynamicMeta: MultiDynamicMeta[];
  incrementDecrement: (i: number, value: number) => void;
  updateQty: (i: number, id: string) => void;
  onCat3Focus: (i: number) => void;
  onCat4Focus: (i: number) => void;
  resolveIndex: (row: PMGridRow) => number;
  toggleExpand: (row: PMGridRow) => void;
}

/**
 * Context for the product sub-rows rendered inline under an expanded placeholder
 * row (they share the parent grid's header/columns; see the Category 4 / Month /
 * dynamic / edit-save cells which are the only editable child affordances).
 */
interface ChildCellCtx {
  currency: string;
  monthOptions: IdNameOption[];
  isArchived: boolean;
  addEditAccess: boolean;
  subBrandAccess: boolean;
  childHandleChange: (val: unknown, path: string) => void;
  childHandleDynamicChange: (path: { keyName: string; value: unknown }[], pIndex: number, cIndex: number, dyIndex: number) => void;
  childHandleMultiDynamicChange: (colId: string, valIds: string[], pIndex: number, cIndex: number) => void;
  multiDynamicMeta: MultiDynamicMeta[];
  dynamicMeta: DynamicMeta[];
  editProduct: (devId: string) => void;
  savePlaceholderProduct: (pIndex: number) => void;
}

type PMCellParams = BombiixCellRendererParams<PMGridRow> & {
  getCtx: () => CellCtx;
  getChildCtx?: () => ChildCellCtx;
  colKey?: string;
};

const optionLabel = (opt: IdNameOption): string => String(opt?.name ?? '');
const optionValue = (opt: IdNameOption): string => String(opt?._id ?? '');
const dynamicLabel = (opt: PlaceholderDynamicColumn): string => String(opt?.keyName ?? '');
const dynamicValue = (opt: PlaceholderDynamicColumn): string => String(opt?._id ?? '');
const portalTarget = (): HTMLElement | null => (typeof document !== 'undefined' ? document.body : null);

type CatRel = Array<{ _id?: string } | string> | { _id?: string } | string | null | undefined;

/**
 * Tolerant category-relation membership test. Accepts a singular relation
 * (`categoryOneId`) or an array relation (`catOneIds`), whose elements may be
 * string ids or `{ _id }` objects, and checks whether `catId` is present.
 * Mirrors the ProductModal.js `some((id) => (id?._id || id) === cat)` pattern so
 * the grid can filter the globally-loaded category masters on the frontend.
 */
const catRelMatch = (singular: CatRel, arr: CatRel, catId: string | undefined): boolean => {
  if (!catId) return false;
  const rel = singular || arr || [];
  const list = Array.isArray(rel) ? rel : [rel];
  return list.some((relItem) => String((relItem as { _id?: string })?._id ?? relItem) === String(catId));
};

const filterCat3 = (list: CategoryMasterOption[], cat1?: string, cat2?: string): CategoryMasterOption[] =>
  (list || []).filter((cat) => catRelMatch(cat.categoryOneId, cat.catOneIds, cat1) && catRelMatch(cat.categoryTwoId, cat.catTwoIds, cat2));

const filterCat4 = (list: CategoryMasterOption[], cat1?: string, cat2?: string, cat3?: string): CategoryMasterOption[] =>
  (list || []).filter(
    (cat) =>
      catRelMatch(cat.categoryOneId, cat.catOneIds, cat1) &&
      catRelMatch(cat.categoryTwoId, cat.catTwoIds, cat2) &&
      catRelMatch(cat.categoryThreeId, cat.catThreeIds, cat3),
  );

const isRowEditable = (row: PMGridRow, isArchived: boolean): boolean => !isArchived && Boolean(row?.isAddRow || row?.placeholderStatus === 'draft');

/** A custom attribute is editable on a row only when its configured product type AND brand match the row's. */
const isDynamicAttributeEnabled = (eleDynamic: DynEle, row: PMGridRow): boolean =>
  Boolean(eleDynamic?.productType?.includes(idOf(row?.productTypeId) as string) && eleDynamic?.brand?.includes(idOf(row?.brandId) as string));

const isChoiceAttribute = (eleDynamic: DynEle): boolean => {
  const attrType = eleDynamic?.type ?? eleDynamic?.colId?.type;
  return attrType === 'dropdown' || attrType === 'radio-button';
};

/**
 * Value the fill handle copies for a custom-attribute column: the chosen option for
 * dropdown / radio attributes, the raw text (or ISO date) for the rest. Also serves as
 * the column's `valueGetter`, so AG Grid can diff and repaint these `dyn:` cells.
 */
const dynamicFillValue = (row: PMGridRow | undefined, colId: string): unknown => {
  const eleDynamic = ((row?.dynamicColumn || []) as DynEle[]).find((d) => d?._id === colId);
  if (!eleDynamic) return null;
  if (isChoiceAttribute(eleDynamic)) return eleDynamic.selectedOpt ?? eleDynamic.valueId ?? null;
  return (eleDynamic.valueText as string) ?? '';
};

/**
 * Value the fill handle just dropped onto a grid row, before the state commit.
 *
 * `dyn:` / `mdyn:` columns have no backing field, so their `valueSetter` parks the
 * dropped value under the column key on the (per-render) grid row copy. The
 * `valueGetter` must report that parked value, because AG Grid re-reads the cell
 * after the setter and only raises `cellValueChanged` — which drives the commit —
 * when the re-read differs from the old value. The next state commit rebuilds the
 * row copies, so the parked value never outlives the commit it triggers.
 */
const fillOverride = (row: PMGridRow | undefined, field: string): unknown => (row as Record<string, unknown> | undefined)?.[field];

/** Selected option ids of a Multi Custom Attribute column, the value the fill handle copies. */
const multiDynamicFillValue = (row: PMGridRow | undefined, colId: string): string[] => {
  const entry = ((row?.multiCustomAttribute || []) as PlaceholderMultiDynamicColumn[]).find((e) => idOf(e?.colId) === colId);
  return ((entry?.valId || []) as Array<{ _id?: string } | string>).map((v) => String(idOf(v)));
};

/**
 * Applies one fill-handle drop onto a row's custom attribute in place, returning whether
 * anything changed. Rows whose attribute is disabled (product type / brand mismatch) or
 * that lack the option being copied are left untouched.
 */
const applyDynamicFill = (row: PMGridRow, colId: string, newValue: unknown): boolean => {
  const list = (row.dynamicColumn || []) as DynEle[];
  const eleDynamic = list.find((d) => d?._id === colId);
  if (!eleDynamic || !isDynamicAttributeEnabled(eleDynamic, row)) return false;
  if (isChoiceAttribute(eleDynamic)) {
    const option = choiceFillValue(newValue, (eleDynamic.options || []) as Array<{ _id?: string; keyName?: string }>);
    if (option === undefined) return false;
    const choice = eleDynamic as unknown as { valueId: IdNameOption | null; selectedOpt: IdNameOption | null; lableId?: string };
    choice.valueId = option;
    choice.selectedOpt = option;
    if (eleDynamic._id) choice.lableId = eleDynamic._id;
    return true;
  }
  eleDynamic.valueText = newValue == null ? '' : String(newValue);
  return true;
};

const errorFor = (ctx: CellCtx, row: PMGridRow, index: number, field: string): string | undefined => {
  const key = (row?._id as string) || index;
  const raw = ctx.globalError?.[key]?.[field];
  if (!raw) return undefined;
  const asObj = raw as unknown as { message?: string };
  return typeof asObj === 'object' && asObj?.message ? asObj.message : String(raw);
};

/* ---------------------------------------------------------------------------
 * Product sub-row (child) cell renderers. These render the expanded product
 * rows inline under the SAME parent grid header/columns (ports the old
 * PlaceholderExpand.js <tr> layout — no nested table). Only Category 4, Month
 * and the custom-attribute cells are editable; the rest are read-only display.
 * ------------------------------------------------------------------------- */

/**
 * Disabled display select for a product sub-row column that shows a value but
 * is not editable (Product Type, Category 1-3, Brand, Sub Brand, Season, Year).
 */
function ChildDisabledSelect(row: PMGridRow, field: string) {
  const value = (row[field] as IdNameOption | null) ?? null;
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled
      options={value ? [value] : []}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
    />
  );
}

/**
 * Editable Category 4 select for a product sub-row, bound to the row's own
 * cat4Data list.
 */
function ChildCat4(row: PMGridRow, ctx: ChildCellCtx) {
  const pIndex = row.__parentIndex as number;
  const cIndex = row.__childIndex as number;
  return (
    <NewSelectMultiSelect<IdNameOption>
      options={(row.cat4Data as IdNameOption[]) || []}
      value={(row.catFourId as IdNameOption | null) ?? null}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      onChange={(val) => ctx.childHandleChange(val, productListPath(pIndex, cIndex, 'catFourId').COMMONPATH)}
    />
  );
}

/**
 * Editable Month select for a product sub-row.
 */
function ChildMonth(row: PMGridRow, ctx: ChildCellCtx) {
  const pIndex = row.__parentIndex as number;
  const cIndex = row.__childIndex as number;
  return (
    <NewSelectMultiSelect<IdNameOption>
      options={ctx.monthOptions || []}
      value={(row.month as IdNameOption | null) ?? null}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      onChange={(val) => ctx.childHandleChange(val, productListPath(pIndex, cIndex, 'month').COMMONPATH)}
    />
  );
}

/** Pause (ms) after the last keystroke before a buffered cell value is committed to grid state. */
const CELL_COMMIT_DELAY_MS = 250;

/**
 * Buffers a text cell's value locally so every keystroke paints immediately,
 * and commits the buffered value to grid state on a short typing pause, on
 * blur, and on unmount.
 *
 * Committing on every keystroke re-runs `gridRowData` and re-renders every
 * editable row (each holding several react-select cells), which blocked the
 * main thread long enough that typed characters showed up late. Committing
 * once per pause keeps the typed text instant; the blur flush runs before a
 * Save click is handled (mousedown blurs first), so nothing is lost on save.
 *
 * `value` is only synced INTO the buffer while unfocused — never forced back
 * on blur — because these cells only repaint via BombiixAGGrid's manual
 * `refreshCells`, which skips the focused column, so the prop can briefly be
 * stale right after typing.
 */
function useBufferedCellValue(value: string, onCommit: (next: string) => void, delay = CELL_COMMIT_DELAY_MS) {
  const [local, setLocal] = useState(value);
  const focusedRef = useRef(false);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current === null) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    commitRef.current(next);
  }, []);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value);
  }, [value]);

  useEffect(() => flush, [flush]);

  const onChange = useCallback(
    (next: string) => {
      setLocal(next);
      pendingRef.current = next;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(flush, delay);
    },
    [flush, delay],
  );
  const onFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);
  const onBlur = useCallback(() => {
    focusedRef.current = false;
    flush();
  }, [flush]);

  return { local, onChange, onFocus, onBlur };
}

interface BufferedTextInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  /** Highlight the field once `charLimit` is exceeded (exceedable custom attributes). */
  highlightOnExceed?: boolean | undefined;
  charLimit?: number | undefined;
  maxLength?: number | undefined;
  type?: string | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
  style?: React.CSSProperties | undefined;
  startAdornment?: React.ReactNode;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement> | undefined;
  /** Normalise a keystroke's raw value before it is buffered; return `null` to reject it. */
  sanitize?: ((raw: string) => string | null) | undefined;
}

/**
 * Free-text grid cell (product description, price, quantity, text custom
 * attributes) backed by `useBufferedCellValue`. The exceeded-char-limit
 * highlight is computed from the local buffer, not from row data, so it
 * updates live while typing rather than only on blur.
 */
function BufferedTextInput({
  value,
  onChange,
  disabled,
  invalid,
  highlightOnExceed,
  charLimit,
  maxLength,
  type,
  placeholder,
  className,
  style,
  startAdornment,
  onKeyDown,
  sanitize,
}: BufferedTextInputProps) {
  const buffered = useBufferedCellValue(value, onChange);
  const exceeded = Boolean(highlightOnExceed) && charLimit != null && buffered.local.length > charLimit;

  return (
    <NewInput
      type={type}
      maxLength={maxLength}
      disabled={disabled}
      invalid={Boolean(invalid) || exceeded}
      value={buffered.local}
      placeholder={placeholder}
      className={className}
      style={style}
      startAdornment={startAdornment}
      onKeyDown={onKeyDown}
      onFocus={buffered.onFocus}
      onBlur={buffered.onBlur}
      onChange={(e) => {
        const next = sanitize ? sanitize(e.target.value) : e.target.value;
        if (next !== null) buffered.onChange(next);
      }}
    />
  );
}

/**
 * Radio-button custom-attribute group. Tracks the checked option in local
 * state for the same reason `BufferedTextInput` buffers keystrokes: these
 * `dyn:` columns only repaint via BombiixAGGrid's manual `refreshCells`, which
 * skips the currently-focused column, so a plain `checked={selectedId === id}`
 * read off row data wouldn't show a selection until focus moved to another
 * column.
 */
function DynamicRadioGroup({
  className,
  idPrefix,
  options,
  selectedId,
  disabled,
  onSelect,
}: {
  className: string;
  idPrefix: string;
  options: PlaceholderDynamicColumn[];
  selectedId: string | null;
  disabled?: boolean | undefined;
  onSelect: (opt: PlaceholderDynamicColumn) => void;
}) {
  const focusedRef = useRef(false);
  const [localSelected, setLocalSelected] = useState(selectedId);

  useEffect(() => {
    if (!focusedRef.current) setLocalSelected(selectedId);
  }, [selectedId]);

  return (
    <div
      className={className}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
      }}
    >
      {options.map((opt) => (
        <NewRadio
          key={dynamicValue(opt)}
          id={`${idPrefix}-${dynamicValue(opt)}`}
          name={idPrefix}
          label={dynamicLabel(opt)}
          checked={localSelected === dynamicValue(opt)}
          disabled={disabled}
          onChange={() => {
            setLocalSelected(dynamicValue(opt));
            onSelect(opt);
          }}
        />
      ))}
    </div>
  );
}

/**
 * Dropdown custom-attribute select. Reflects the pick optimistically in local
 * state so the selected label appears on the same click instead of waiting on
 * BombiixAGGrid's post-change `refreshCells` sweep, which skips the currently
 * -focused column. Mirrors `SelectInputCell`'s pattern (same react-select +
 * AG-Grid + `document.body` portal setup) — no focus-tracking gate, since the
 * portaled menu makes DOM focus/blur timing around a pick unreliable.
 */
function DynamicDropdown({
  isDisabled = false,
  options,
  value,
  onChange,
}: {
  isDisabled?: boolean;
  options: PlaceholderDynamicColumn[];
  value: PlaceholderDynamicColumn | null;
  onChange: (opt: PlaceholderDynamicColumn | null) => void;
}) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <NewSelectMultiSelect<PlaceholderDynamicColumn>
      isDisabled={isDisabled}
      options={options}
      value={localValue}
      getOptionLabel={dynamicLabel}
      getOptionValue={dynamicValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      onChange={(val) => {
        const next = (val as PlaceholderDynamicColumn | null) ?? null;
        setLocalValue(next);
        onChange(next);
      }}
    />
  );
}

/**
 * Editable custom-attribute cell for a product sub-row (dropdown, date, radio
 * or free-text based on the attribute type).
 */
function ChildDynamic(row: PMGridRow, ctx: ChildCellCtx, colKey: string) {
  const pIndex = row.__parentIndex as number;
  const cIndex = row.__childIndex as number;
  const colId = colKey.replace('dyn:', '');
  const list = (row?.dynamicColumn || []) as DynEle[];
  const dyIndex = list.findIndex((d) => d?._id === colId);
  const eleDynamic = dyIndex >= 0 ? list[dyIndex] : undefined;
  if (!eleDynamic || !eleDynamic.isActive || !(eleDynamic as { isChecked?: boolean }).isChecked) return null;
  const attrType = eleDynamic?.type ?? eleDynamic?.colId?.type;
  const dpCfg = (eleDynamic?.datePickerConfig ??
    (eleDynamic?.colId as { datePickerConfig?: { isEditable?: boolean; dateFormat?: string } } | undefined)?.datePickerConfig ??
    {}) as { isEditable?: boolean; dateFormat?: string };
  const textCfg = eleDynamic?.textFieldConfig;
  const valueId = eleDynamic?.valueId as IdNameOption | string | null | undefined;
  const selectedRadioId = (valueId as IdNameOption)?._id || (valueId as string) || null;
  /* A product's own dynamicColumn entry never carries `options` (the API only populates
     colId/valueId with keyName+type, not the label's option list) — fall back to the
     placeholder-level label config, which does. Mirrors how ChildMultiDynamicCell sources
     its options from ctx.multiDynamicMeta instead of the row's own entry. */
  const configOptions = ctx.dynamicMeta?.find((m) => m._id === colId)?.options;
  const resolvedOptions = (eleDynamic?.options?.length ? eleDynamic.options : configOptions) as PlaceholderDynamicColumn[] | undefined;

  if (attrType === 'dropdown') {
    return (
      <DynamicDropdown
        options={(resolvedOptions || []).filter((option) => option.isActive)}
        value={(eleDynamic?.valueId as unknown as PlaceholderDynamicColumn) ?? null}
        onChange={(val) => ctx.childHandleDynamicChange([{ keyName: 'valueId', value: val }], pIndex, cIndex, dyIndex)}
      />
    );
  }
  if (attrType === 'date-picker') {
    return (
      <NewDatePicker
        value={eleDynamic?.valueText ? moment(eleDynamic.valueText as string, 'YYYY-MM-DD').toDate() : null}
        disabled={dpCfg.isEditable === false}
        dateFormat={(dpCfg.dateFormat || 'DD-MMM-YYYY').replace(/Y/g, 'y').replace(/D/g, 'd')}
        placeholder={eleDynamic?.keyName || ''}
        autoApply
        portalId={DATEPICKER_PORTAL_ID}
        onChange={(d) => ctx.childHandleDynamicChange([{ keyName: 'valueText', value: d ? moment(d).format('YYYY-MM-DD') : '' }], pIndex, cIndex, dyIndex)}
      />
    );
  }
  if (attrType === 'radio-button') {
    const radioOptions = (resolvedOptions || []).filter((o) => o.isActive || o._id === selectedRadioId);
    return (
      <DynamicRadioGroup
        className="custom-attribute-radio-group"
        idPrefix={`phx-radio-${eleDynamic._id}-${cIndex}-${dyIndex}`}
        options={radioOptions}
        selectedId={selectedRadioId}
        onSelect={(opt) => ctx.childHandleDynamicChange([{ keyName: 'valueId', value: dynamicValue(opt) }], pIndex, cIndex, dyIndex)}
      />
    );
  }
  const maxlen = textCfg && !textCfg.isExceedable ? textCfg.charLimit : undefined;
  return (
    <BufferedTextInput
      maxLength={maxlen}
      disabled={!textCfg?.isEditable}
      highlightOnExceed={Boolean(textCfg?.isExceedable) && Boolean(textCfg?.isHighlightable)}
      charLimit={textCfg?.charLimit}
      value={(eleDynamic?.valueText as string) ?? ''}
      onChange={(val) => ctx.childHandleDynamicChange([{ keyName: 'valueText', value: val }], pIndex, cIndex, dyIndex)}
    />
  );
}

/**
 * Edit + Save actions for a product sub-row (Save renders on the first child
 * only, matching the original layout).
 */
function ChildActions(row: PMGridRow, ctx: ChildCellCtx) {
  if (!ctx.addEditAccess) return null;
  const pIndex = row.__parentIndex as number;
  const cIndex = row.__childIndex as number;
  return (
    <div className="d-flex">
      <button
        className="bombiix-icon-container bombiix-icon-container--blue"
        type="button"
        onClick={() => ctx.editProduct(row?._id as string)}
        disabled={ctx.isArchived}
        aria-label="Edit product"
      >
        <EditIcon size={24} />
      </button>
      {cIndex === 0 && (
        <button
          type="button"
          className="bombiix-icon-container bombiix-icon-container--red"
          onClick={() => ctx.savePlaceholderProduct(pIndex)}
          disabled={ctx.isArchived}
          aria-label="Save products"
        >
          <SaveIcon size={24} />
        </button>
      )}
    </div>
  );
}

/**
 * Per-row selection checkbox — mirrors the draft/non-default checkbox column
 * from the original hand-built table.
 */
function SelectionCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return null;
  const ctx = params.getCtx();
  if (!(row?.placeholderStatus === 'draft' && !row.isDefault)) return null;
  const checked = Boolean(row.__selected);
  return <NewCheckbox size="sm" checked={checked} onChange={() => ctx.handleSelection(row?._id as string)} aria-label="Select row" />;
}

/**
 * Product Ref column. On a master placeholder row it renders the expand/collapse
 * chevron (which lazily fetches and inlines the product sub-rows); on a product
 * sub-row it renders the shortened product number.
 */
function ProductRefCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return <span>{shortenandHoverString(row?.number || '--', 12)}</span>;
  if (row?.placeholderStatus !== 'published' || row?.isAddRow) return null;
  const ctx = params.getCtx();
  const expanded = Boolean(row?.isExpand);
  return (
    <button
      type="button"
      className="bombiix-icon-container"
      onClick={() => ctx.toggleExpand(row)}
      aria-label={expanded ? 'Collapse row' : 'Expand row'}
      title={expanded ? 'Collapse row' : 'Expand row'}
    >
      <ChevronIcon size={24} className={expanded ? 'icon-inverse' : undefined} />
    </button>
  );
}

/**
 * Product Type select. When category is driven by Product Type it triggers a
 * dependent-category reset cascade via handleChange.
 */
function ProductTypeCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'supplierType');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={ctx.productTypeOptions || []}
      value={(row.productTypeId as IdNameOption | null) ?? null}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      invalid={Boolean(errorFor(ctx, row, index, 'productTypeId'))}
      onChange={(val) =>
        ctx.catBasedOnPTData
          ? ctx.handleChange(val, placeholderManagementPath(index, 'productTypeId').COMMONPATH, index)
          : ctx.handleChange(val, placeholderManagementPath(index, 'productTypeId').COMMONPATH)
      }
    />
  );
}

const cat1OptionsFor = (row: PMGridRow, ctx: CellCtx): CategoryMasterOption[] => {
  if (!ctx.catBasedOnPTData) return (ctx.category1Options || []).filter((re) => re?.isActive);
  const selectedPT = idOf(row?.productTypeId);
  if (!selectedPT) return [];
  return (ctx.category1Options || []).filter((re) => {
    const rel = re?.productType || [];
    const arr = Array.isArray(rel) ? rel : [rel];
    const matchesPT = arr.some((pt) => idOf(pt as { _id?: string } | string) === selectedPT);
    return re?.isActive && matchesPT;
  });
};

const cat2OptionsFor = (row: PMGridRow, ctx: CellCtx): CategoryMasterOption[] => {
  if (!ctx.catBasedOnPTData) return (ctx.category2Options || []).filter((re) => re?.isActive);
  const selectedPT = idOf(row?.productTypeId);
  const selectedCat1 = idOf(row?.catOneId);
  if (!selectedPT || !selectedCat1) return [];
  return (ctx.category2Options || []).filter((re) => {
    const usedList = re?.isUsedCat1;
    const linkedList = re?.catOneIds;
    const matchesCat1 =
      (Array.isArray(usedList) && usedList.some((id) => idOf(id as { _id?: string } | string) === selectedCat1)) ||
      (Array.isArray(linkedList) && linkedList.some((id) => idOf(id as { _id?: string } | string) === selectedCat1));
    return re?.isActive && (matchesCat1 || (!Array.isArray(usedList) && !Array.isArray(linkedList)));
  });
};

const resolveSelected = (opts: IdNameOption[], current: IdNameOption | string | null | undefined, fallbackName?: string): IdNameOption | null => {
  const currentId = idOf(current);
  const selected = opts?.find((o) => idOf(o) === currentId);
  if (selected) return selected;
  if (current && typeof current === 'object' && current?.name) return current;
  return currentId ? { _id: currentId, name: (current as IdNameOption)?.name || fallbackName || '(inactive)' } : null;
};

/**
 * Category 1 select with Product-Type-aware option filtering.
 */
function Cat1Cell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'catOneId');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const opts = cat1OptionsFor(row, ctx) as unknown as IdNameOption[];
  const value = ctx.catBasedOnPTData && !idOf(row?.productTypeId) ? null : resolveSelected(opts, row?.catOneId ?? null, row?.catOneName);
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={opts}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      invalid={Boolean(errorFor(ctx, row, index, 'catOneId'))}
      onChange={(val) => ctx.handleChange(val, placeholderManagementPath(index, 'catOneId').COMMONPATH, index)}
    />
  );
}

/**
 * Category 2 select cascaded from Category 1 (and Product Type when enabled).
 */
function Cat2Cell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'catTwoId');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const selectedCat1 = idOf(row?.catOneId);
  const opts = cat2OptionsFor(row, ctx) as unknown as IdNameOption[];
  let value: IdNameOption | null = null;
  if (selectedCat1 && !(ctx.catBasedOnPTData && !idOf(row?.productTypeId))) {
    value = resolveSelected(opts, row?.catTwoId ?? null, row?.catTwoName);
  }
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={opts}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      invalid={Boolean(errorFor(ctx, row, index, 'catTwoId'))}
      onChange={(val) => ctx.handleChange(val, placeholderManagementPath(index, 'catTwoId').COMMONPATH, index)}
    />
  );
}

const cat3OptionsFor = (row: PMGridRow, ctx: CellCtx): IdNameOption[] => {
  const cat1Id = idOf(row?.catOneId);
  const cat2Id = idOf(row?.catTwoId);
  const rowOpts = (row?.category3Options || []) as unknown as IdNameOption[];
  if (rowOpts?.length) return rowOpts;
  return filterCat3(ctx.category3Options || [], cat1Id, cat2Id) as unknown as IdNameOption[];
};

const cat4OptionsFor = (row: PMGridRow, ctx: CellCtx): IdNameOption[] => {
  const cat1Id = idOf(row?.catOneId);
  const cat2Id = idOf(row?.catTwoId);
  const cat3Id = idOf(row?.catThreeId);
  const rowOpts = (row?.category4Options || []) as unknown as IdNameOption[];
  if (rowOpts?.length) return rowOpts;
  return filterCat4(ctx.category4Options || [], cat1Id, cat2Id, cat3Id) as unknown as IdNameOption[];
};

/**
 * Category 3 select — reads the row's own options list (populated by the
 * cascade / masters effect) and refreshes them on open.
 */
function Cat3Cell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'catThreeId');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const opts = cat3OptionsFor(row, ctx);
  const value = resolveSelected(opts, row?.catThreeId ?? null, row?.catThreeName);
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={opts}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      invalid={Boolean(errorFor(ctx, row, index, 'catThreeId'))}
      onChange={(val) => ctx.handleChange(val, placeholderManagementPath(index, 'catThreeId').COMMONPATH, index)}
    />
  );
}

/**
 * Category 4 select — reads the row's own options list and refreshes on open.
 */
function Cat4Cell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) {
    const cctx = params.getChildCtx?.();
    return cctx ? ChildCat4(row, cctx) : null;
  }
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const opts = cat4OptionsFor(row, ctx);
  const value = resolveSelected(opts, row?.catFourId ?? null, row?.catFourName);
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={opts}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      onChange={(val) => ctx.handleChange(val, placeholderManagementPath(index, 'catFourId').COMMONPATH, index)}
    />
  );
}

/**
 * Product description free-text cell routed through the grid onRowChange.
 */
function ProductDescCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return <NewInput disabled value={(row.description as string) ?? ''} />;
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const rowId = String(row.__gridId ?? row._id ?? '');
  return (
    <BufferedTextInput
      disabled={disabled}
      value={(row.productDesc as string) ?? ''}
      invalid={Boolean(errorFor(ctx, row, index, 'productDesc'))}
      onChange={(next) => params.onRowChange?.(rowId, 'productDesc', next)}
    />
  );
}

/**
 * Brand select. Selecting a brand clears the row's sub-brand (matches the
 * original inline handler; no duplicate / dynamic recalculation).
 */
function BrandCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'brandId');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={ctx.brandOptions || []}
      value={(row.brandId as IdNameOption | null) ?? null}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      invalid={Boolean(errorFor(ctx, row, index, 'brandId'))}
      onChange={(val) => ctx.handleBrandChange(val, index)}
    />
  );
}

/**
 * Sub Brand select filtered by the selected brand.
 */
function SubBrandCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'subBrandId');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const brandId = idOf(row.brandId);
  const opts = (ctx.subBrandOptions || []).filter((opt) => opt.brandId === brandId && opt.isActive === true);
  const value = (ctx.subBrandOptions || []).find((opt) => opt._id === idOf(row.subBrandId)) || null;
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={opts}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      onChange={(val) => ctx.handleChange(val, placeholderManagementPath(index, 'subBrandId').COMMONPATH)}
    />
  );
}

/**
 * Season select.
 */
function SeasonCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'seasonId');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={ctx.seasonOptions || []}
      value={(row.seasonId as IdNameOption | null) ?? null}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      invalid={Boolean(errorFor(ctx, row, index, 'seasonId'))}
      onChange={(val) => ctx.handleChange(val, placeholderManagementPath(index, 'seasonId').COMMONPATH, index)}
    />
  );
}

/**
 * Month select. Stores the month id (string) via handleChange.
 */
function MonthCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) {
    const cctx = params.getChildCtx?.();
    return cctx ? ChildMonth(row, cctx) : null;
  }
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const value = row?.month ? (ctx.monthOptions || []).find((val) => val?._id === row?.month) ?? null : null;
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={ctx.monthOptions || []}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      onChange={(val) => ctx.handleChange((val as IdNameOption)?._id, placeholderManagementPath(index, 'month').COMMONPATH, index)}
    />
  );
}

/**
 * Year select. Stores the year name (string) via handleChange.
 */
function YearCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return ChildDisabledSelect(row, 'year');
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const disabled = !isRowEditable(row, ctx.isArchived);
  const value = row?.year ? { _id: row?.year as string, name: row?.year as string } : null;
  return (
    <NewSelectMultiSelect<IdNameOption>
      isDisabled={disabled}
      options={ctx.yearOptions || []}
      value={value}
      getOptionLabel={optionLabel}
      getOptionValue={optionValue}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      invalid={Boolean(errorFor(ctx, row, index, 'year'))}
      scrollToLabel={getCurrentYearLabel()}
      onChange={(val) => ctx.handleChange((val as IdNameOption)?.name, placeholderManagementPath(index, 'year').COMMONPATH, index)}
    />
  );
}

/**
 * Custom-attribute (dynamic column) cell for a placeholder row.
 */
function DynamicCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) {
    const cctx = params.getChildCtx?.();
    return cctx ? ChildDynamic(row, cctx, params.colKey || '') : null;
  }
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const colId = (params.colKey || '').replace('dyn:', '');
  const list = (row?.dynamicColumn || []) as DynEle[];
  const dyIndex = list.findIndex((d) => d?._id === colId);
  const eleDynamic = dyIndex >= 0 ? list[dyIndex] : undefined;
  if (!eleDynamic || !eleDynamic.isActive || !eleDynamic.isChecked) return null;
  const isEnabled = isDynamicAttributeEnabled(eleDynamic, row);
  const attrType = eleDynamic?.type ?? eleDynamic?.colId?.type;
  const dpCfg = (eleDynamic?.datePickerConfig ??
    (eleDynamic?.colId as { datePickerConfig?: { isEditable?: boolean; dateFormat?: string; isRequired?: boolean } } | undefined)?.datePickerConfig ??
    {}) as { isEditable?: boolean; dateFormat?: string; isRequired?: boolean };
  const textCfg = eleDynamic?.textFieldConfig;
  const selectedOpt = eleDynamic?.selectedOpt as IdNameOption | undefined;
  const valueId = eleDynamic?.valueId as IdNameOption | string | null | undefined;
  const selectedRadioId = selectedOpt?._id || (valueId as IdNameOption)?._id || (valueId as string) || null;

  if (attrType === 'dropdown') {
    return (
      <DynamicDropdown
        isDisabled={!isEnabled}
        options={(eleDynamic?.options || []).filter((option) => option.isActive)}
        value={isEnabled ? (eleDynamic?.selectedOpt as unknown as PlaceholderDynamicColumn) ?? null : null}
        onChange={(val) =>
          ctx.handleDynamicChange(
            [
              { keyName: 'valueId', value: val },
              { keyName: 'selectedOpt', value: val },
              { keyName: 'lableId', value: eleDynamic._id },
            ],
            index,
            dyIndex,
          )
        }
      />
    );
  }
  if (attrType === 'date-picker') {
    return (
      <NewDatePicker
        value={isEnabled && eleDynamic?.valueText ? moment(eleDynamic.valueText as string, 'YYYY-MM-DD').toDate() : null}
        disabled={!isEnabled || dpCfg.isEditable === false}
        dateFormat={(dpCfg.dateFormat || 'DD-MMM-YYYY').replace(/Y/g, 'y').replace(/D/g, 'd')}
        placeholder={eleDynamic?.keyName || ''}
        autoApply
        portalId={DATEPICKER_PORTAL_ID}
        onChange={(d) => ctx.handleDynamicChange([{ keyName: 'valueText', value: d ? moment(d).format('YYYY-MM-DD') : '' }], index, dyIndex)}
      />
    );
  }
  if (attrType === 'radio-button') {
    const radioOptions = (eleDynamic?.options || []).filter((o) => o.isActive || o._id === selectedRadioId);
    return (
      <DynamicRadioGroup
        className="custom-attr-radio-list"
        idPrefix={`pl-radio-${eleDynamic._id}-${index}-${dyIndex}`}
        options={radioOptions}
        selectedId={selectedRadioId}
        disabled={!isEnabled}
        onSelect={(opt) =>
          ctx.handleDynamicChange(
            [
              { keyName: 'valueId', value: opt },
              { keyName: 'selectedOpt', value: opt },
              { keyName: 'lableId', value: eleDynamic._id },
            ],
            index,
            dyIndex,
          )
        }
      />
    );
  }
  const maxlen = textCfg && !textCfg.isExceedable ? textCfg.charLimit : undefined;
  return (
    <BufferedTextInput
      maxLength={maxlen}
      disabled={!isEnabled}
      highlightOnExceed={Boolean(textCfg?.isExceedable) && Boolean(textCfg?.isHighlightable)}
      charLimit={textCfg?.charLimit}
      value={isEnabled ? (eleDynamic.valueText as string) || '' : ''}
      onChange={(val) => ctx.handleDynamicChange([{ keyName: 'valueText', value: val }], index, dyIndex)}
    />
  );
}

/**
 * Resolves a row's current selection + the offered option list for one Multi
 * Custom Attribute column. The selected `value` is matched against the FULL
 * option list (not just the active ones) so a since-deactivated-but-previously
 * -chosen option still renders as a chip instead of silently vanishing — the
 * `options` list offered for further picking is active-only.
 */
const resolveMultiDynamicSelection = (
  row: PMGridRow,
  meta: MultiDynamicMeta | undefined,
  colId: string,
): { options: Array<{ _id: string; keyName?: string }>; value: Array<{ _id: string; keyName?: string }> } => {
  const allOptions = meta?.options || [];
  const options = allOptions.filter((opt) => opt.isActive !== false);
  const entries = (row?.multiCustomAttribute || []) as PlaceholderMultiDynamicColumn[];
  const entry = entries.find((e) => idOf(e?.colId) === colId);
  const selectedIds = (entry?.valId || []).map((v) => idOf(v));
  const value = allOptions.filter((opt) => selectedIds.includes(opt._id));
  return { options, value };
};

const toSelectedMultiDynamicIds = (selected: unknown): string[] => {
  if (!selected) return [];
  const arr = Array.isArray(selected) ? selected : [selected];
  return arr.map((opt) => String((opt as { _id?: string })?._id));
};

type MultiDynamicOption = { _id: string; keyName?: string };

interface MultiDynamicSelectCellProps {
  options: MultiDynamicOption[];
  value: MultiDynamicOption[];
  isEnabled: boolean;
  onCommit: (valIds: string[]) => void;
}

function MultiDynamicSelectCell({ options, value, isEnabled, onCommit }: MultiDynamicSelectCellProps) {
  const [pending, setPending] = useState<MultiDynamicOption[]>(isEnabled ? value : []);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const valueKey = useMemo(
    () =>
      value
        .map((opt) => opt._id)
        .sort()
        .join(','),
    [value],
  );

  useEffect(() => {
    setPending(isEnabled ? value : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey, isEnabled]);

  const commit = useCallback(
    (ids: string[]) => {
      if ([...ids].sort().join(',') === valueKey) return;
      onCommit(ids);
    },
    [onCommit, valueKey],
  );

  return (
    <NewSelectMultiSelect<MultiDynamicOption>
      isMulti
      isDisabled={!isEnabled}
      options={options}
      value={pending}
      getOptionLabel={(opt) => String(opt?.keyName ?? '')}
      getOptionValue={(opt) => String(opt?._id ?? '')}
      menuPosition="fixed"
      menuPortalTarget={portalTarget()}
      onChange={(selected, actionMeta) => {
        const ids = toSelectedMultiDynamicIds(selected);
        setPending(options.filter((opt) => ids.includes(opt._id)));
        const action = (actionMeta as { action?: string } | undefined)?.action;
        if (action !== 'select-option' && action !== 'deselect-option') {
          commit(ids);
        }
      }}
      onMenuClose={() => commit(pendingRef.current.map((opt) => opt._id))}
    />
  );
}

/**
 * Multi Custom Attribute (multi-select dynamic column) cell for a product sub-row.
 */
function ChildMultiDynamicCell({ row, ctx, colKey }: { row: PMGridRow; ctx: ChildCellCtx; colKey: string }) {
  const pIndex = row.__parentIndex as number;
  const cIndex = row.__childIndex as number;
  const colId = colKey.replace('mdyn:', '');
  const meta = ctx.multiDynamicMeta?.find((m) => m._id === colId);
  if (!meta) return null;
  const isEnabled = isMultiDynamicEnabledForRow(meta, idOf(row?.supplierType as unknown as { _id?: string } | string));
  const { options, value } = resolveMultiDynamicSelection(row, meta, colId);
  return (
    <MultiDynamicSelectCell
      options={options}
      value={value}
      isEnabled={isEnabled}
      onCommit={(ids) => ctx.childHandleMultiDynamicChange(colId, ids, pIndex, cIndex)}
    />
  );
}

/**
 * Multi Custom Attribute (multi-select dynamic column) cell for a placeholder row.
 */
function MultiDynamicCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) {
    const cctx = params.getChildCtx?.();
    return cctx ? <ChildMultiDynamicCell row={row} ctx={cctx} colKey={params.colKey || ''} /> : null;
  }
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const colId = (params.colKey || '').replace('mdyn:', '');
  const meta = ctx.multiDynamicMeta?.find((m) => m._id === colId);
  if (!meta) return null;
  const isEnabled = isMultiDynamicEnabledForRow(meta, idOf(row?.productTypeId));
  const { options, value } = resolveMultiDynamicSelection(row, meta, colId);
  return <MultiDynamicSelectCell options={options} value={value} isEnabled={isEnabled} onCommit={(ids) => ctx.handleMultiDynamicChange(colId, ids, index)} />;
}

/**
 * Placeholder quantity cell — numeric input, up/down steppers, and an Update
 * action when the value exceeds the persisted quantity.
 */
function PlaceholderQtyCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return <span>-</span>;
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const editable = isRowEditable(row, ctx.isArchived);
  const rowId = String(row.__gridId ?? row._id ?? '');
  const placeholderAty = row?.placeholderQuantity && Number(row?.placeholderQuantity) > 0 ? parseInt(String(row?.placeholderQuantity), 10) : 0;
  const persisted = ctx.placeholderList?.find((val) => val?._id === row?._id);
  const persistedAty = persisted?.placeholderQuantity && Number(persisted?.placeholderQuantity) > 0 ? parseInt(String(persisted?.placeholderQuantity), 10) : 0;
  const disabledDecrement = placeholderAty === persistedAty;
  const showUpdateBtn = placeholderAty > persistedAty;
  const showSplit = showUpdateBtn && !row?.isAddRow;
  return (
    <div className={`d-flex align-items-center w-100 ${showSplit ? ' qty-cell-split' : ''}`}>
      <div className="input-btn-container w-100 ">
        <BufferedTextInput
          type="number"
          className={showUpdateBtn ? 'placeholder-qty-input--compact' : undefined}
          disabled={!editable}
          value={String(row?.placeholderQuantity ?? '')}
          onChange={(next) => params.onRowChange?.(rowId, 'placeholderQuantity', next)}
          onKeyDown={(e) => {
            const isValidKey = /^\d+$/.test(e.key) || e.key === 'Backspace' || e.key === 'Delete';
            if (!isValidKey) e.preventDefault();
          }}
        />
        {Number(row.placeholderQuantity) >= 0 && !row?.isAddRow && (
          <div className="up-down-btn">
            <button
              type="button"
              className="btn position-relative"
              disabled={ctx.isArchived}
              aria-label="Increment"
              onClick={() => ctx.incrementDecrement(index, parseInt(String(row.placeholderQuantity), 10) + 1)}
            >
              {/* 16, not 24: .icon-dropdown is absolutely positioned at 5px inside the small
                  increment/decrement button; a larger glyph overflows it. */}
              <ChevronIcon size={16} className="icon-dropdown icon-inverse" />
            </button>
            <button
              type="button"
              className="btn position-relative"
              disabled={ctx.isArchived || disabledDecrement}
              aria-label="Decrement"
              onClick={() => ctx.incrementDecrement(index, parseInt(String(row.placeholderQuantity), 10) - 1)}
            >
              <ChevronIcon size={16} className="icon-dropdown" />
            </button>
          </div>
        )}
      </div>
      {showSplit && (
        <button className="bombiix-btn btn-light ml-1" type="button" disabled={ctx.isArchived} onClick={() => ctx.updateQty(index, row._id as string)}>
          Update
        </button>
      )}
    </div>
  );
}

/**
 * Actual quantity + fulfilment ratio, with a duplicate-row warning icon.
 */
function ActualQtyCell(params: PMCellParams) {
  const row = params?.data;
  if (!row) return null;
  if (row.__isChild) return <span>-</span>;
  const ctx = params.getCtx();
  const index = ctx.resolveIndex(row);
  const actual = row?.actualQuantity ? Number(row?.actualQuantity) : 0;
  const planned = Number(row?.placeholderQuantity);
  const ratio = !planned ? 0 : round((actual / planned) * 100, 2);
  return (
    <div className="d-flex align-items-center justify-content-between">
      <span>
        {actual} ({ratio}%)
      </span>
      {ctx.duplicateExist.includes(index) && (
        <span title="Column should be unique." className="ml-3 text-danger d-inline-flex">
          <InfoIcon size={24} />
        </span>
      )}
    </div>
  );
}

/**
 * Status dot + duplicate marker shown in the trailing row-actions cell.
 */
function StatusExtras(status: string | undefined) {
  switch (status) {
    case 'published':
      return <span className="col-status right approved" />;
    default:
      return <span className="col-status right pending" />;
  }
}

/**
 * Editable placeholder detail grid. Ports List.js to BombiixAGGrid with
 * per-row cascading category selects; expanding a placeholder row inlines its
 * product sub-rows under the same grid header (no nested table).
 */
function PlaceholderGrid(props: PlaceholderGridProps): React.ReactElement {
  const { handleSelection, mainState, placeholderAddEditAccess, placeholderDeleteAccess, subBrandAccess } = props;
  const routeParams = useParams<{ placeholderId?: string; id?: string }>();
  const placeholderId = routeParams.placeholderId ?? routeParams.id ?? '';
  const history = useHistory();
  const localStorageFilter = localStorageService.get(`placeholderFilter_${placeholderId}`);
  const dispatch = useDispatch<import('redux-thunk').ThunkDispatch<unknown, unknown, import('redux').AnyAction>>();
  const { t } = useTranslation('common');
  const reducer = useSelector(
    (state: {
      UserDetails: { userInfo: { userId: string; companyCurrency?: string } };
      Measuremets: { CountList: unknown };
      PlaceholderDetails: {
        placeholderList: Array<{ _id?: string; placeholderQuantity?: string | number }>;
        multiCustomAttribute: MultiDynamicColumnMaster[];
      };
      SystemMastersDetails: { catOptions3?: CategoryMasterOption[]; catOptions4?: CategoryMasterOption[] };
    }) => state,
  );
  const { userInfo } = reducer.UserDetails;
  const { CountList } = reducer.Measuremets;
  const { placeholderList, multiCustomAttribute } = reducer.PlaceholderDetails;
  const { catOptions3, catOptions4 } = reducer.SystemMastersDetails;

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState<{ show: boolean; value: Record<string, unknown> | null; placholderUpdateIndex: number }>({
    show: false,
    value: null,
    placholderUpdateIndex: -1,
  });
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>('');
  const [isLoadingConf, setIsLoadingConf] = useState(false);
  const [childModal, setChildModal] = useState<{
    devOrderId: string | null;
    isReplaceModalOpen: boolean;
  }>({ devOrderId: null, isReplaceModalOpen: false });
  const gridKeyRef = useRef(0);

  const {
    placeholderState,
    placeholderState: {
      placeholderManagement,
      category1Options,
      category2Options,
      category3Options,
      category4Options,
      yearOptions,
      monthOptions,
      seasonOptions,
      productTypeOptions,
      brandOptions,
      subBrandOptions,
      duplicateExist,
      loader,
      isPageLoader,
      hasMore,
      pageSize,
    },
    setPlaceholderState,
    converDropdownData,
    checkDuplicate,
    getUpdatedProducts,
    globalError,
    individualDetail,
  } = usePlaceholderState();

  const currencySymbol = (currencySymbols as Record<string, { symbol?: string }>)?.[userInfo?.companyCurrency as string]?.symbol || '';
  console.log('placeholderId', placeholderId);
  useEffect(() => {
    if (placeholderId) {
      dispatch(getIndividualDetails(placeholderId));
    }
  }, [placeholderId, dispatch]);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(catOptions3) || catOptions3.length === 0) dispatch(getTypeBasedCatgeory({ categoryType: 3, isActive: true }));
    if (!Array.isArray(catOptions4) || catOptions4.length === 0) dispatch(getTypeBasedCatgeory({ categoryType: 4, isActive: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(catOptions3) && catOptions3.length > 0) {
      setPlaceholderState((prevState) => ({ ...prevState, category3Options: cloneDeep(catOptions3) }));
    }
  }, [catOptions3, setPlaceholderState]);

  useEffect(() => {
    if (Array.isArray(catOptions4) && catOptions4.length > 0) {
      setPlaceholderState((prevState) => ({ ...prevState, category4Options: cloneDeep(catOptions4) }));
    }
  }, [catOptions4, setPlaceholderState]);

  useEffect(() => {
    if (!Array.isArray(placeholderManagement) || placeholderManagement.length === 0) return;
    const hasCat3Master = Array.isArray(category3Options) && category3Options.length > 0;
    const hasCat4Master = Array.isArray(category4Options) && category4Options.length > 0;
    if (!hasCat3Master && !hasCat4Master) return;
    const copyState = cloneDeep(placeholderState);
    let changed = false;
    (placeholderManagement as PMGridRow[]).forEach((row, i) => {
      const cat1Id = idOf(row?.catOneId);
      const cat2Id = idOf(row?.catTwoId);
      const cat3Id = idOf(row?.catThreeId);
      if (hasCat3Master && (!Array.isArray(row?.category3Options) || row?.category3Options.length === 0) && cat1Id && cat2Id) {
        const cat3Opts = filterCat3(category3Options as CategoryMasterOption[], cat1Id, cat2Id);
        if (cat3Opts.length > 0) {
          set(copyState, placeholderManagementPath(i, 'category3Options').COMMONPATH, cloneDeep(cat3Opts));
          changed = true;
        }
      }
      if (hasCat4Master && (!Array.isArray(row?.category4Options) || row?.category4Options.length === 0) && cat1Id && cat2Id && cat3Id) {
        const cat4Opts = filterCat4(category4Options as CategoryMasterOption[], cat1Id, cat2Id, cat3Id);
        if (cat4Opts.length > 0) {
          set(copyState, placeholderManagementPath(i, 'category4Options').COMMONPATH, cloneDeep(cat4Opts));
          changed = true;
        }
      }
    });
    if (changed) setPlaceholderState((prev) => ({ ...prev, ...copyState }));
  }, [category3Options, category4Options, placeholderManagement, setPlaceholderState, placeholderState]);

  const getCat3Data = useCallback(
    (cat1: string, cat2: string): CategoryMasterOption[] => filterCat3(category3Options as CategoryMasterOption[], cat1, cat2),
    [category3Options],
  );

  const getCat4Data = useCallback(
    (cat1: string, cat2: string, cat3: string): CategoryMasterOption[] => filterCat4(category4Options as CategoryMasterOption[], cat1, cat2, cat3),
    [category4Options],
  );

  const insert = (arr: PMGridRow[], index: number, newItem: PMGridRow): PMGridRow[] => [...arr.slice(0, index), newItem, ...arr.slice(index)];

  const placeholderStateRef = useRef(placeholderState);
  placeholderStateRef.current = placeholderState;

  const resolveIndexById = useCallback((rowId: BombiixRowId): number => {
    const pm = placeholderStateRef.current.placeholderManagement as PMGridRow[];
    return pm.findIndex((r) => String(r.__gridId ?? r._id ?? '') === String(rowId));
  }, []);

  const resolveIndexByRow = useCallback((row: PMGridRow): number => {
    const pm = placeholderStateRef.current.placeholderManagement as PMGridRow[];
    return pm.findIndex((r) => (r.__gridId && r.__gridId === row.__gridId) || (r._id && r._id === row._id));
  }, []);

  const addRow = useCallback(
    async (index: number) => {
      const copyState = placeholderStateRef.current;
      const getCopyObj = cloneDeep(get(copyState, placeholderManagementPath(index, '').GET_CURRENT_INDEX_OBJECT, {})) as PMGridRow;
      if (!isEmpty(getCopyObj)) {
        const arr = cloneDeep(get(copyState, 'placeholderManagement', [])) as PMGridRow[];
        delete getCopyObj._id;
        delete getCopyObj.placeholderNo;
        delete getCopyObj.actualQuantity;
        delete getCopyObj.productList;
        delete getCopyObj.__gridId;
        getCopyObj.isAddRow = true;
        const newArr = insert(arr, index + 1, getCopyObj);
        set(copyState, 'placeholderManagement', newArr);
        const cat1Id = idOf(getCopyObj?.catOneId);
        const cat2Id = idOf(getCopyObj?.catTwoId);
        const cat3Id = idOf(getCopyObj?.catThreeId);
        const cat3Data = getCat3Data(cat1Id as string, cat2Id as string);
        set(copyState, placeholderManagementPath(index + 1, 'category3Options').COMMONPATH, cloneDeep(cat3Data));
        const cat4Data = getCat4Data(cat1Id as string, cat2Id as string, cat3Id as string);
        set(copyState, placeholderManagementPath(index + 1, 'category4Options').COMMONPATH, cloneDeep(cat4Data));
        const checkDup = await checkDuplicate(cloneDeep(newArr));
        set(copyState, 'duplicateExist', checkDup.unique);
        setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
      }
    },
    [getCat3Data, getCat4Data, checkDuplicate, setPlaceholderState],
  );

  const handleDelete = useCallback(
    async (id: string | number | null) => {
      const copyState = placeholderStateRef.current.placeholderManagement as PMGridRow[];
      if (typeof id === 'number' || id === null) {
        copyState.splice(id as number, 1);
        const checkDup = await checkDuplicate(copyState);
        setPlaceholderState((prevState) => ({ ...prevState, placeholderManagement: copyState, duplicateExist: checkDup.unique }));
      } else {
        setDeleteId(id as string);
        setShowConfirmation(true);
      }
    },
    [checkDuplicate, setPlaceholderState],
  );

  const deleteConfirmed = useCallback(async () => {
    setIsLoadingConf(true);
    const objPara = { placeholderID: deleteId };
    setPlaceholderState((prevState) => ({ ...prevState, loader: true, placeholderManagement: [] }));
    await dispatch(deletePlaceholderData(objPara, t));
    await dispatch(clearReducer());
    setDeleteId(null);
    setIsLoadingConf(false);
    setShowConfirmation(false);
    let res = null;
    const params = {
      pageIndex: 1,
      pageSize,
      isScrolled: true,
      seasonsId: individualDetail?.season?.map((item) => item._id),
      month: individualDetail?.month,
      year: individualDetail?.year,
      category1: individualDetail?.catOneId?.map((item) => item._id),
      category2: individualDetail?.catTwoId?.map((item) => item._id),
      productTypeId: individualDetail?.productType?.map((item) => item._id),
      brandId: individualDetail?.brand?.map((item) => item._id),
      subBrandId: individualDetail?.subBrand?.map((item) => item._id),
    };
    if (localStorageFilter) {
      localStorageFilter.pageIndex = 1;
      localStorageFilter.pageSize = pageSize;
      res = await dispatch(getPlaceholderData(localStorageFilter));
    } else {
      res = await dispatch(getPlaceholderData(params));
    }
    if (res) setPlaceholderState((prevState) => ({ ...prevState, loader: false }));
  }, [deleteId, dispatch, t, pageSize, individualDetail, localStorageFilter, setPlaceholderState]);

  const updateQty = useCallback(
    (ind: number, id: string) => {
      const qty = get(placeholderStateRef.current, placeholderManagementPath(ind, 'placeholderQuantity').COMMONPATH, 0);
      const objPara = { placeholderID: id, placeholderQuantity: qty, CountNumber: CountList };
      setShowUpdatePopup((prev) => ({ ...prev, show: true, value: objPara, placholderUpdateIndex: ind }));
    },
    [CountList],
  );

  const handleExpandFetch = useCallback(
    async (index: number) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      const currObj = get(copyState, placeholderManagementPath(index, '').GET_CURRENT_INDEX_OBJECT, null) as PMGridRow | null;
      if (!currObj) return;
      const placeholderProductList = (await dispatch(fetchPlaceholderProduct({ placeholderID: currObj?._id }))) as unknown as PMGridRow[];
      let cat3Data: CategoryMasterOption[] | null = null;
      if (currObj?.catOneId && currObj?.catTwoId) cat3Data = getCat3Data(idOf(currObj?.catOneId) as string, idOf(currObj?.catTwoId) as string);
      const manipuLatedDynamicColumn = await converDropdownData((currObj?.dynamicColumn as PlaceholderDynamicColumn[]) ?? []);
      if (checkArray(placeholderProductList)) {
        for (const placehold of placeholderProductList as Array<Record<string, unknown>>) {
          let cat4Data: CategoryMasterOption[] | null = null;
          if (currObj?.catOneId && currObj?.catTwoId && placehold?.catThreeId) {
            cat4Data = getCat4Data(idOf(currObj?.catOneId) as string, idOf(currObj?.catTwoId) as string, placehold?.catThreeId as string);
          }
          const newDynamicColumn: PlaceholderDynamicColumn[] = [];
          const deletedDyna: PlaceholderDynamicColumn[] = [];
          placehold.cat3Data = cat3Data;
          placehold.cat4Data = cat4Data;
          placehold.month = placehold?.month ? { _id: placehold?.month, name: placehold?.month } : null;
          placehold.year = placehold?.year ? { _id: placehold?.year, name: placehold?.year } : null;
          (placehold?.dynamicColumn as PlaceholderDynamicColumn[])?.forEach((placeDy) => {
            const updatedData = placeDy;
            const foundDAta = manipuLatedDynamicColumn.find((val) => val?._id === updatedData?.colId?._id);
            if (!foundDAta) {
              updatedData.isDeleted = true;
              deletedDyna.push(updatedData);
            }
          });
          manipuLatedDynamicColumn?.forEach((dy) => {
            const foundDAta = (placehold?.dynamicColumn as PlaceholderDynamicColumn[])?.find((val) => val?.colId?._id === dy._id);
            if (foundDAta) {
              const foundNewObj = { ...dy, ...foundDAta };
              if (dy?.options) foundNewObj.options = dy?.options;
              newDynamicColumn.push(foundNewObj);
            } else {
              newDynamicColumn.push({
                ...dy,
                colId: { isLable: dy?.isLable, keyName: dy?.keyName, refName: dy?.refName, seqNumber: dy?.seqNumber, type: dy?.type, _id: dy?._id },
              });
            }
          });
          placehold.dynamicColumn = newDynamicColumn;
          placehold.deletedColumn = deletedDyna;
        }
      }
      currObj.productList = cloneDeep(placeholderProductList);
      currObj.productListClone = cloneDeep(placeholderProductList);
      set(copyState, placeholderManagementPath(index, 'isExpand').COMMONPATH, true);
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [dispatch, getCat3Data, getCat4Data, converDropdownData, setPlaceholderState],
  );

  const toggleExpand = useCallback(
    (row: PMGridRow) => {
      const index = resolveIndexByRow(row);
      if (index < 0) return;
      const current = (placeholderStateRef.current.placeholderManagement as PMGridRow[])[index];
      if (current?.productList) {
        const copyState = cloneDeep(placeholderStateRef.current);
        set(copyState, placeholderManagementPath(index, 'isExpand').COMMONPATH, !current?.isExpand);
        setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
      } else {
        handleExpandFetch(index);
      }
    },
    [resolveIndexByRow, setPlaceholderState, handleExpandFetch],
  );

  const childHandleChange = useCallback(
    (val: unknown, path: string) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      set(copyState, path, val);
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [setPlaceholderState],
  );

  const childHandleDynamicChange = useCallback(
    (path: { keyName: string; value: unknown }[], pIndex: number, cIndex: number, dyIndex: number) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      const dynamicArray = cloneDeep(get(copyState, productListDynamicPath(pIndex, cIndex, dyIndex, '').GET_DYNAMIC_COLUMN_PATH, [])) as unknown[];
      path.forEach((dat) => {
        set(dynamicArray, `[${dyIndex}][${dat.keyName}]`, dat.value);
      });
      set(copyState, productListDynamicPath(pIndex, cIndex, dyIndex, '').GET_DYNAMIC_COLUMN_PATH, dynamicArray);
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [setPlaceholderState],
  );

  /**
   * Replaces a product sub-row's selected values for one Multi Custom
   * Attribute column in one shot (multi-select `onChange` always carries the
   * full new selection, unlike dynamicColumn's per-keyname updates).
   */
  const childHandleMultiDynamicChange = useCallback(
    (colId: string, valIds: string[], pIndex: number, cIndex: number) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      const path = productListMultiDynamicColumnPath(pIndex, cIndex).MULTI_DYNAMIC_COLUMN_PATH;
      const entries = cloneDeep(get(copyState, path, [])) as PlaceholderMultiDynamicColumn[];
      set(copyState, path, upsertMultiDynamicEntry(entries, colId, valIds));
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [setPlaceholderState],
  );

  const savePlaceholderProduct = useCallback(
    async (pIndex: number) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      const parentPlaceholder = cloneDeep(get(copyState, `placeholderManagement[${pIndex}]`, {})) as {
        productList?: PlaceholderRow[];
        productListClone?: PlaceholderRow[];
      };
      const finalArray = await getUpdatedProducts(
        (parentPlaceholder?.productListClone as PlaceholderRow[]) ?? [],
        (parentPlaceholder?.productList as PlaceholderRow[]) ?? [],
      );
      const requesDat: Record<string, unknown>[] = [];
      for (const exp of finalArray as Array<Record<string, unknown>>) {
        const obj: Record<string, unknown> = {
          catThreeId: (exp?.catThreeId as { _id?: string })?._id,
          catFourId: (exp?.catFourId as { _id?: string })?._id,
          description: exp.description,
          brandId: (exp?.brandId as { _id?: string })?._id,
          month: (exp?.month as { name?: string })?.name,
          _id: exp._id,
        };
        obj.dynamicColumn = (exp?.dynamicColumn as DynEle[] | undefined)?.map((childEle) => {
          if (childEle?.valueId || childEle?.valueText) {
            return {
              colId: childEle?.colId?._id,
              valueId: (childEle?.valueId as unknown as { _id?: string })?._id ? (childEle?.valueId as unknown as { _id?: string })?._id : null,
              valueText: childEle?.valueText ? childEle?.valueText : '',
            };
          }
          return { colId: childEle?.colId?._id, valueId: null, valueText: '' };
        });
        obj.multiCustomAttribute = (exp?.multiCustomAttribute as PlaceholderMultiDynamicColumn[] | undefined)?.map((multiEle) => ({
          colId: idOf(multiEle?.colId),
          valId: (multiEle?.valId || []).map((v) => idOf(v)),
        }));
        requesDat.push(obj);
      }
      if (checkArray(requesDat)) {
        const res = (await dispatch(savePlaceholderProductAction(requesDat as unknown as Record<string, unknown>))) as unknown as { code?: number };
        if (res?.code === 200) {
          const cs = cloneDeep(placeholderStateRef.current);
          set(cs, `placeholderManagement[${pIndex}].productList`, parentPlaceholder?.productList);
          set(cs, `placeholderManagement[${pIndex}].productListClone`, cloneDeep(parentPlaceholder?.productList));
          setPlaceholderState((prevState) => ({ ...prevState, ...cs }));
        }
      } else {
        toasterMsg('Make changes in product to update', 'danger');
      }
    },
    [getUpdatedProducts, dispatch, setPlaceholderState],
  );

  const editProduct = useCallback(
    (devId: string) => {
      history.push(`${PLACEHOLDER_ROUTES.PRODUCT_EDIT}/${devId}?placeholderId=${placeholderId}`, {
        returnTo: `${PLACEHOLDER_ROUTES.CARD_EDIT}/${placeholderId}`,
      });
    },
    [history, placeholderId],
  );

  const handleChildReplaceClose = useCallback(() => {
    setChildModal((prev) => ({ ...prev, isReplaceModalOpen: false, devOrderId: null }));
  }, []);

  const updatePlaceholderQty = useCallback(async () => {
    setIsLoadingConf(true);
    const obj = showUpdatePopup?.value as Record<string, unknown>;
    const res = (await dispatch(updateQtyAction(obj, t))) as unknown as { code?: number };
    if (res?.code === 200) {
      await dispatch(getCounterValueNumber());
      const idx = showUpdatePopup?.placholderUpdateIndex;
      if (idx >= 0) {
        const plaholderObj = (placeholderStateRef.current.placeholderManagement as PMGridRow[])?.[idx];
        const params = {
          pageIndex: 1,
          pageSize,
          seasonsId: individualDetail?.season?.map((item) => item._id),
          month: individualDetail?.month,
          year: individualDetail?.year,
          category1: individualDetail?.catOneId?.map((item) => item._id),
          category2: individualDetail?.catTwoId?.map((item) => item._id),
          productTypeId: individualDetail?.productType?.map((item) => item._id),
          brandId: individualDetail?.brand?.map((item) => item._id),
          subBrandId: individualDetail?.subBrand?.map((item) => item._id),
        };
        if (localStorageFilter) await dispatch(getPlaceholderData({ ...params, ...localStorageFilter }));
        else await dispatch(getPlaceholderData(params));
        if (plaholderObj?.isExpand) await handleExpandFetch(idx);
      }
    }
    setIsLoadingConf(false);
    setShowUpdatePopup((prev) => ({ ...prev, show: false, value: null, placholderUpdateIndex: -1 }));
  }, [showUpdatePopup, dispatch, t, pageSize, individualDetail, localStorageFilter, handleExpandFetch]);

  const handleChange = useCallback<ChangeFn>(
    async (val, path, i) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      if (path.includes('subBrandId')) set(copyState, path, (val as IdNameOption)?._id || null);
      else set(copyState, path, val);
      if (path.includes('brandId')) {
        set(copyState, placeholderManagementPath(i as number, 'subBrandId').COMMONPATH, null);
        set(copyState, placeholderManagementPath(i as number, 'subBrandOptions').COMMONPATH, []);
      }
      if (path.includes('productTypeId') && catBasedOnPTData) {
        set(copyState, placeholderManagementPath(i as number, 'catOneId').COMMONPATH, null);
        set(copyState, placeholderManagementPath(i as number, 'catTwoId').COMMONPATH, null);
        set(copyState, placeholderManagementPath(i as number, 'catThreeId').COMMONPATH, null);
        set(copyState, placeholderManagementPath(i as number, 'catFourId').COMMONPATH, null);
        set(copyState, placeholderManagementPath(i as number, 'category3Options').COMMONPATH, []);
        set(copyState, placeholderManagementPath(i as number, 'category4Options').COMMONPATH, []);
      }
      if (['catOneId', 'catTwoId'].some((v) => path.includes(v))) {
        if (path.includes('catOneId')) set(copyState, placeholderManagementPath(i as number, 'catTwoId').COMMONPATH, null);
        set(copyState, placeholderManagementPath(i as number, 'catThreeId').COMMONPATH, null);
        set(copyState, placeholderManagementPath(i as number, 'catFourId').COMMONPATH, null);
      }
      const productType = get(copyState, placeholderManagementPath(i as number, 'productTypeId').COMMONPATH) as IdNameOption | undefined;
      const brand = get(copyState, placeholderManagementPath(i as number, 'brandId').COMMONPATH) as IdNameOption | undefined;
      if (['productTypeId', 'brandId'].some((v) => path.includes(v))) {
        set(copyState, placeholderManagementPath(i as number, 'dynamicColumn').COMMONPATH, []);
        if (productType?._id && brand?._id) {
          const res = (await dispatch(getCustomAttributeFreeText({ productType: productType?._id, brand: brand?._id }))) as unknown as {
            data?: Array<Record<string, unknown>>;
          };
          const customAttr = res?.data?.map((attr) => ({ ...attr, valueText: '', selectedOpt: null })) || [];
          set(copyState, placeholderManagementPath(i as number, 'dynamicColumn').COMMONPATH, customAttr);
        }
      }
      if (path.includes('productTypeId')) {
        set(copyState, multiDynamicColumnPath(i as number).MULTI_DYNAMIC_COLUMN_PATH, []);
      }
      if (['catOneId', 'catTwoId', 'catThreeId'].some((v) => path.includes(v))) {
        const cat1 = get(copyState, placeholderManagementPath(i as number, 'catOneId').COMMONPATH) as IdNameOption | undefined;
        const cat2 = get(copyState, placeholderManagementPath(i as number, 'catTwoId').COMMONPATH) as IdNameOption | undefined;
        const cat3 = get(copyState, placeholderManagementPath(i as number, 'catThreeId').COMMONPATH) as IdNameOption | undefined;
        if (cat1 && cat2 && cat3) {
          const categoryRes = filterCat4(category4Options as CategoryMasterOption[], cat1?._id, cat2?._id, cat3?._id);
          set(copyState, placeholderManagementPath(i as number, 'category4Options').COMMONPATH, cloneDeep(categoryRes));
        } else if (cat1 && cat2) {
          const categoryRes = filterCat3(category3Options as CategoryMasterOption[], cat1?._id, cat2?._id);
          set(copyState, placeholderManagementPath(i as number, 'category3Options').COMMONPATH, cloneDeep(categoryRes));
        }
      }
      const chekDup = await checkDuplicate((copyState.placeholderManagement as PMGridRow[]) ?? []);
      set(copyState, 'duplicateExist', chekDup?.unique);
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [catBasedOnPTData, dispatch, category3Options, category4Options, checkDuplicate, setPlaceholderState],
  );

  const handleBrandChange = useCallback(
    (val: unknown, i: number) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      set(copyState, placeholderManagementPath(i, 'brandId').COMMONPATH, val);
      set(copyState, placeholderManagementPath(i, 'subBrandId').COMMONPATH, null);
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [setPlaceholderState],
  );

  const handleDynamicChange = useCallback(
    (path: { keyName: string; value: unknown }[], i: number, dyIn: number) => {
      const copyState = placeholderStateRef.current;
      path.forEach((dat) => {
        set(copyState, dynamicColumnPath(i, dyIn, dat.keyName).DYNAMIC_COLUMN_PATH_KEYNAME, dat.value);
      });
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [setPlaceholderState],
  );

  /**
   * Replaces a placeholder row's selected values for one Multi Custom
   * Attribute column in one shot.
   */
  const handleMultiDynamicChange = useCallback(
    (colId: string, valIds: string[], i: number) => {
      const copyState = cloneDeep(placeholderStateRef.current);
      const path = multiDynamicColumnPath(i).MULTI_DYNAMIC_COLUMN_PATH;
      const entries = cloneDeep(get(copyState, path, [])) as PlaceholderMultiDynamicColumn[];
      set(copyState, path, upsertMultiDynamicEntry(entries, colId, valIds));
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [setPlaceholderState],
  );

  const incrementDecrement = useCallback(
    (i: number, value: number) => {
      const copyState = placeholderStateRef.current;
      set(copyState, placeholderManagementPath(i, 'placeholderQuantity').COMMONPATH, value);
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [setPlaceholderState],
  );

  const onCat3Focus = useCallback(
    (i: number) => {
      const copyState = placeholderStateRef.current;
      const cat1 = get(copyState, placeholderManagementPath(i, 'catOneId').COMMONPATH) as IdNameOption | undefined;
      const cat2 = get(copyState, placeholderManagementPath(i, 'catTwoId').COMMONPATH) as IdNameOption | undefined;
      if (cat1 && cat2) {
        const categoryRes = getCat3Data(cat1?._id, cat2?._id);
        set(copyState, placeholderManagementPath(i, 'category3Options').COMMONPATH, cloneDeep(categoryRes));
        setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
      }
    },
    [getCat3Data, setPlaceholderState],
  );

  const onCat4Focus = useCallback(
    (i: number) => {
      const copyState = placeholderStateRef.current;
      const cat1 = get(copyState, placeholderManagementPath(i, 'catOneId').COMMONPATH) as IdNameOption | undefined;
      const cat2 = get(copyState, placeholderManagementPath(i, 'catTwoId').COMMONPATH) as IdNameOption | undefined;
      const cat3 = get(copyState, placeholderManagementPath(i, 'catThreeId').COMMONPATH) as IdNameOption | undefined;
      const categoryRes = getCat4Data(cat1?._id as string, cat2?._id as string, cat3?._id as string);
      set(copyState, placeholderManagementPath(i, 'category4Options').COMMONPATH, cloneDeep(categoryRes));
      setPlaceholderState((prevState) => ({ ...prevState, ...copyState }));
    },
    [getCat4Data, setPlaceholderState],
  );

  const handlePageNextInScroll = useCallback(async () => {
    let pageIndex = placeholderStateRef.current?.pageIndex;
    pageIndex += 1;
    setPlaceholderState((nextPagestate) => ({ ...nextPagestate, pageIndex, isPageLoader: true }));
    const params = {
      pageIndex,
      pageSize,
      isScrolled: true,
      seasonsId: individualDetail?.season?.map((item) => item._id),
      month: individualDetail?.month,
      year: individualDetail?.year,
      category1: individualDetail?.catOneId?.map((item) => item._id),
      category2: individualDetail?.catTwoId?.map((item) => item._id),
      productTypeId: individualDetail?.productType?.map((item) => item._id),
      brandId: individualDetail?.brand?.map((item) => item._id),
      subBrandId: individualDetail?.subBrand?.map((item) => item._id),
    };
    const res = await dispatch(getPlaceholderData(params));
    if (res) setPlaceholderState((prevState) => ({ ...prevState, isPageLoader: false }));
  }, [pageSize, individualDetail, dispatch, setPlaceholderState]);

  /**
   * Free-text cell change (description / quantity).
   *
   * Applies the keystroke as a functional update against the LATEST state and
   * only touches the edited row. The generic `handleChange` path deep-clones a
   * render-time ref snapshot and awaits before committing, so two quick
   * keystrokes both cloned the same stale row and the second overwrote the
   * first — letters were dropped or re-ordered while typing fast (BM-9887).
   * The duplicate-row check is recomputed from the committed rows afterwards.
   */
  const handleRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      if (!FREE_TEXT_ROW_FIELDS.includes(field)) return;
      const index = resolveIndexById(rowId);
      if (index < 0) return;
      setPlaceholderState((prevState) => {
        const rows = [...((prevState.placeholderManagement as PMGridRow[]) ?? [])];
        if (!rows[index]) return prevState;
        rows[index] = { ...rows[index], [field]: value };
        checkDuplicate(rows).then((duplicates) =>
          setPlaceholderState((latest) => ({ ...latest, duplicateExist: duplicates.unique })),
        );
        return { ...prevState, placeholderManagement: rows };
      });
    },
    [resolveIndexById, setPlaceholderState, checkDuplicate],
  );

  const gridRowData = useMemo<PMGridRow[]>(() => {
    const rows: PMGridRow[] = [];
    const selectedIds = mainState?.selectedIdsArray ?? [];
    (placeholderManagement as PMGridRow[]).forEach((row, pIndex) => {
      if (!row.__gridId) row.__gridId = (row._id as string) || `pm-${gridKeyRef.current++}`;
      const isEditableRow = Boolean(row.isAddRow) || row.placeholderStatus === 'draft';
      if (isEditableRow) {
        rows.push({ ...row, __selected: selectedIds.includes(row._id as string) });
      } else {
        rows.push(row);
      }
      if (row.isExpand && Array.isArray(row.productList)) {
        (row.productList as PMGridRow[]).forEach((child, cIndex) => {
          rows.push({
            ...child,
            __isChild: true,
            __parentIndex: pIndex,
            __childIndex: cIndex,
            __gridId: `phx-${row.__gridId}-${cIndex}`,
          });
        });
      }
    });
    return rows;
  }, [placeholderState, mainState, globalError]);

  const dynamicColumnRaw = (placeholderManagement?.[0]?.dynamicColumn as DynEle[]) || [];
  const dynamicColumnSignature = JSON.stringify(
    dynamicColumnRaw.map((ele) => ({
      id: ele?._id,
      isActive: ele?.isActive,
      isChecked: (ele as { isChecked?: boolean })?.isChecked,
      type: ele?.type ?? ele?.colId?.type,
      productType: ele?.productType,
      brand: ele?.brand,
    })),
  );

  const dynamicColumnConfig = useMemo<DynEle[]>(
    () => dynamicColumnRaw,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dynamicColumnSignature],
  );

  const dynamicMeta = useMemo<DynamicMeta[]>(() => {
    const first = dynamicColumnConfig;
    const selectedProductTypeIds = (individualDetail?.productType || []).map((item) => item._id);
    const selectedBrandIds = (individualDetail?.brand || []).map((item) => item._id);
    const noSelection = selectedProductTypeIds.length === 0 && selectedBrandIds.length === 0;
    return first
      .filter((ele) => ele?.isActive && (ele as { isChecked?: boolean })?.isChecked)
      .filter(
        (ele) =>
          noSelection ||
          ((ele.productType || []).some((id) => selectedProductTypeIds.includes(id)) && (ele.brand || []).some((id) => selectedBrandIds.includes(id))),
      )
      .map((ele) => ({
        _id: String(ele._id),
        keyName: String(ele.keyName ?? ''),
        attrType: String(ele.type ?? ele.colId?.type ?? 'text'),
        options: (ele.options || []).map((opt) => ({ _id: String(opt._id), keyName: opt.keyName ?? '', isActive: opt.isActive ?? true })),
      }));
  }, [dynamicColumnConfig, individualDetail]);

  const multiDynamicMeta = useMemo<MultiDynamicMeta[]>(() => {
    const selectedProductTypeIds = (individualDetail?.productType || []).map((item) => item._id);
    const noSelection = selectedProductTypeIds.length === 0;
    return (multiCustomAttribute || [])
      .filter((label) => label?.isActive)
      .filter((label) => noSelection || (label.productType || []).some((id) => selectedProductTypeIds.includes(id)))
      .map((label) => ({
        _id: String(label._id),
        keyName: String(label.keyName ?? ''),
        options: (label.values || []).map((opt) => ({ _id: String(opt._id), keyName: opt.keyName ?? '', isActive: opt.isActive ?? true })),
        productType: (label.productType || []).map(String),
      }));
  }, [multiCustomAttribute, individualDetail]);

  const ctxRef = useRef<CellCtx>({} as CellCtx);
  ctxRef.current = {
    placeholderManagement: placeholderManagement as PMGridRow[],
    productTypeOptions: productTypeOptions as unknown as IdNameOption[],
    category1Options: category1Options as unknown as CategoryMasterOption[],
    category2Options: category2Options as unknown as CategoryMasterOption[],
    category3Options: category3Options as unknown as CategoryMasterOption[],
    category4Options: category4Options as unknown as CategoryMasterOption[],
    brandOptions: brandOptions as unknown as IdNameOption[],
    subBrandOptions: subBrandOptions as unknown as Array<IdNameOption & { brandId?: string }>,
    seasonOptions: seasonOptions as unknown as IdNameOption[],
    monthOptions: monthOptions as unknown as IdNameOption[],
    yearOptions: yearOptions as unknown as IdNameOption[],
    catBasedOnPTData,
    subBrandAccess: Boolean(subBrandAccess),
    addEditAccess: Boolean(placeholderAddEditAccess),
    deleteAccess: Boolean(placeholderDeleteAccess),
    isArchived: Boolean(individualDetail?.isArchived),
    currencySymbol,
    globalError: globalError as Record<string, Record<string, string>>,
    placeholderList,
    duplicateExist,
    mainState,
    handleSelection,
    handleChange,
    handleBrandChange,
    handleDynamicChange,
    handleMultiDynamicChange,
    multiDynamicMeta,
    incrementDecrement,
    updateQty,
    onCat3Focus,
    onCat4Focus,
    resolveIndex: resolveIndexByRow,
    toggleExpand,
  };
  const getCtx = useCallback(() => ctxRef.current, []);

  const childCtxRef = useRef<ChildCellCtx>({} as ChildCellCtx);
  childCtxRef.current = {
    currency: currencySymbol,
    monthOptions: monthOptions as unknown as IdNameOption[],
    isArchived: Boolean(individualDetail?.isArchived),
    addEditAccess: Boolean(placeholderAddEditAccess),
    subBrandAccess: Boolean(subBrandAccess),
    childHandleChange,
    childHandleDynamicChange,
    childHandleMultiDynamicChange,
    multiDynamicMeta,
    dynamicMeta,
    editProduct,
    savePlaceholderProduct,
  };
  const getChildCtx = useCallback(() => childCtxRef.current, []);

  const isArchivedCard = Boolean(individualDetail?.isArchived);

  /** Only editable placeholder (master) rows may receive a fill-handle copy of a master-row cell. */
  const isFillTargetRow = useCallback((row: PMGridRow): boolean => !row?.__isChild && isRowEditable(row, isArchivedCard), [isArchivedCard]);

  /**
   * Product sub-rows take a fill into the cells they edit (Category 4, Month, custom attributes)
   * while the card is open and the user may edit it. Declared here rather than at the end of the
   * component because the column memo below reads it while rendering.
   */
  const isChildFillTargetRow = useCallback(
    (row: PMGridRow): boolean => Boolean(row?.__isChild) && !isArchivedCard && Boolean(placeholderAddEditAccess),
    [isArchivedCard, placeholderAddEditAccess],
  );

  /**
   * Commits a fill-handle drag (copy a cell's value down onto the rows below) or a paste in one
   * state update. Free-text fields copy verbatim and the quantity only as a whole number; the
   * dropdowns go through `applyMasterFill`, parents first, so each takes only an option its row
   * offers and resets what depends on it; custom attributes go through `applyDynamicFill`, and Multi
   * Custom Attribute columns replace the row's selection for that column. Product sub-rows go
   * through `applyChildFill`, and a drag never crosses between master rows and sub-rows, whose
   * values differ in shape. Rows the fill may not touch are skipped. The duplicate-row check is
   * recomputed from the result (BM-9681). A new product type empties the row's custom attributes at
   * once; they are reloaded afterwards onto the rows as they are by then, so edits made while they
   * load are kept, and the custom-attribute values the same paste carried are applied to them.
   */
  const handleFillCells = useCallback(
    (batch: BombiixFillBatchItem<PMGridRow>[], sourceRows: PMGridRow[]) => {
      const rows = cloneDeep((placeholderStateRef.current.placeholderManagement as PMGridRow[]) ?? []);
      const sourceIsChild = sourceRows.length ? Boolean(sourceRows[0]?.__isChild) : undefined;
      const ctx = getCtx();
      const masterContext: MasterFillContext = {
        optionsFor: (row, field) => masterOptionsFor(row as PMGridRow, field, ctx),
        catBasedOnPTData: ctx.catBasedOnPTData,
        category3OptionsFor: (cat1Id, cat2Id) => filterCat3(ctx.category3Options || [], cat1Id, cat2Id),
        category4OptionsFor: (cat1Id, cat2Id, cat3Id) => filterCat4(ctx.category4Options || [], cat1Id, cat2Id, cat3Id),
      };
      const attributeReloads: AttributeReload[] = [];
      const reloadOf = (row: PMGridRow): AttributeReload | undefined => attributeReloads.find((reload) => reload.gridId === gridIdOf(row));
      let touched = false;
      [...batch]
        .sort((first, second) => masterFillRank(first.field) - masterFillRank(second.field))
        .forEach(({ rowId, field, newValue, data }) => {
        delete (data as Record<string, unknown>)[`fill:${field}`];
        if (sourceIsChild !== undefined && Boolean(data?.__isChild) !== sourceIsChild) return;
        if (data?.__isChild) {
          if (!isChildFillTargetRow(data)) return;
          const child = rows[data.__parentIndex as number]?.productList?.[data.__childIndex as number];
          if (!child) return;
          const childMeta = { monthOptions: monthOptions as unknown as IdNameOption[], dynamicMeta, multiDynamicMeta };
          if (applyChildFill(child as Parameters<typeof applyChildFill>[0], field, newValue, childMeta)) touched = true;
          return;
        }
        const index = resolveIndexById(rowId);
        const row = rows[index];
        if (!row || !isFillTargetRow(row)) return;
        if (MASTER_SELECT_FILL_ORDER.includes(field)) {
          if (!applyMasterFill(row as Parameters<typeof applyMasterFill>[0], field, newValue, masterContext)) return;
          touched = true;
          if (field === 'productTypeId' && !reloadOf(row)) {
            attributeReloads.push({ gridId: gridIdOf(row), productType: idOf(row.productTypeId), brand: idOf(row.brandId), fills: [] });
          }
        } else if (field === 'placeholderQuantity') {
          const quantity = acceptPlaceholderQuantity(newValue);
          if (quantity === undefined) return;
          row.placeholderQuantity = quantity;
          touched = true;
        } else if (FREE_TEXT_ROW_FIELDS.includes(field)) {
          (row as Record<string, unknown>)[field] = newValue == null ? '' : String(newValue);
          touched = true;
        } else if (field.startsWith('dyn:') && reloadOf(row)) {
          reloadOf(row)?.fills.push({ field, newValue });
        } else if (applyMasterAttributeFill(row, field, newValue, multiDynamicMeta)) {
          touched = true;
        }
        });
      if (!touched) return;
      const commitRows = (nextRows: PMGridRow[]): Promise<void> =>
        checkDuplicate(nextRows).then((duplicates) =>
          setPlaceholderState((prevState) => ({ ...prevState, placeholderManagement: nextRows, duplicateExist: duplicates.unique })),
        );
      const fetchAttributes = (productType: string, brand: string): Promise<unknown> =>
        dispatch(getCustomAttributeFreeText({ productType, brand })) as unknown as Promise<unknown>;
      commitRows(rows)
        .then(() =>
          attributeReloads.length
            ? withReloadedAttributes(
                attributeReloads,
                () => (placeholderStateRef.current.placeholderManagement as PMGridRow[]) ?? [],
                fetchAttributes,
                (row, field, newValue) => applyMasterAttributeFill(row, field, newValue, multiDynamicMeta),
              )
            : null,
        )
        .then((reloadedRows) => (reloadedRows ? commitRows(reloadedRows) : undefined))
        .catch(() => toasterMsg(ERROR_MESSAGE, 'danger'));
    },
    [resolveIndexById, isFillTargetRow, isChildFillTargetRow, monthOptions, dynamicMeta, multiDynamicMeta, checkDuplicate, setPlaceholderState, getCtx, dispatch],
  );

  const columnDefs = useMemo<BombiixColumnDef<PMGridRow>[]>(() => {
    /** Master rows, or master and product rows alike for the columns a product row edits too. */
    const masterOrChildRow = (row: PMGridRow): boolean => isFillTargetRow(row) || isChildFillTargetRow(row);

    /**
     * Makes a column whose cell renders its own control take a fill or paste. AG Grid writes the
     * dropped value onto a key of its own, so the cell keeps rendering the stored value until
     * `handleFillCells` commits it, and a copy gives the label the cell shows.
     */
    const ownCellFill = (field: string, fillRows: (row: PMGridRow) => boolean = isFillTargetRow): Partial<BombiixColumnDef<PMGridRow>> => ({
      fillable: true,
      getCellDisabled: (row) => !fillRows(row),
      getClipboardText: (row) => fillCellText(row, field, getCtx()),
      valueGetter: (params) => fillOverride(params.data, `fill:${field}`) ?? (params.data as Record<string, unknown> | undefined)?.[field] ?? null,
      valueSetter: (params) => {
        (params.data as Record<string, unknown>)[`fill:${field}`] = params.newValue;
        return true;
      },
    });

    const cols: BombiixColumnDef<PMGridRow>[] = [
      {
        headerName: '',
        colId: '__pm_select',
        field: '__selected',
        cellRenderer: SelectionCell,
        cellProps: { getCtx },
        width: 50,
        minWidth: 50,
        sortable: false,
        pinned: 'left',
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.PRODUCT_REF'),
        field: 'number',
        cellRenderer: ProductRefCell,
        cellProps: { getCtx },
        width: 120,
        sortable: false,
        pinned: 'left',
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.PRODUCT_TYPE'),
        field: 'productTypeId',
        cellRenderer: ProductTypeCell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('productTypeId'),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CATEGO0RY_1'),
        field: 'catOneId',
        cellRenderer: Cat1Cell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('catOneId'),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CATEGORY2'),
        field: 'catTwoId',
        cellRenderer: Cat2Cell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('catTwoId'),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CATEGORY3'),
        field: 'catThreeId',
        cellRenderer: Cat3Cell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('catThreeId'),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CATEGORY4'),
        field: 'catFourId',
        cellRenderer: Cat4Cell,
        cellProps: { getCtx, getChildCtx },
        width: 150,
        sortable: true,
        ...ownCellFill('catFourId', masterOrChildRow),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.PRODUCT_DES'),
        field: 'productDesc',
        cellRenderer: ProductDescCell,
        cellProps: { getCtx },
        width: 150,
        sortable: false,
        fillable: true,
        getCellDisabled: (row) => !isFillTargetRow(row),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.BRAND'),
        field: 'brandId',
        cellRenderer: BrandCell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('brandId'),
      },
    ];
    if (subBrandAccess) {
      cols.push({
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.SUB_BRAND'),
        field: 'subBrandId',
        cellRenderer: SubBrandCell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('subBrandId'),
      });
    }
    cols.push(
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.SEASON'),
        field: 'seasonId',
        cellRenderer: SeasonCell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('seasonId'),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.MONTH'),
        field: 'month',
        cellRenderer: MonthCell,
        cellProps: { getCtx, getChildCtx },
        width: 150,
        ...ownCellFill('month', masterOrChildRow),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.YEAR'),
        field: 'year',
        cellRenderer: YearCell,
        cellProps: { getCtx },
        width: 150,
        ...ownCellFill('year'),
      },
    );
    dynamicMeta.forEach((meta) => {
      const field = `dyn:${meta._id}`;
      cols.push({
        headerName: meta.keyName,
        colId: field,
        field,
        cellRenderer: DynamicCell,
        cellProps: { getCtx, getChildCtx, colKey: field },
        width: meta.attrType === 'radio-button' ? 240 : 150,
        sortable: false,
        fillable: true,
        getCellDisabled: (row) => !(isFillTargetRow(row) || isChildFillTargetRow(row)),
        valueGetter: (params) => fillOverride(params.data, field) ?? dynamicFillValue(params.data, meta._id),
        valueSetter: (params) => {
          (params.data as Record<string, unknown>)[field] = params.newValue;
          return true;
        },
      });
    });
    multiDynamicMeta.forEach((meta) => {
      const field = `mdyn:${meta._id}`;
      cols.push({
        headerName: meta.keyName,
        colId: field,
        field,
        cellRenderer: MultiDynamicCell,
        cellProps: { getCtx, getChildCtx, colKey: field },
        width: 200,
        sortable: false,
        fillable: true,
        getCellDisabled: (row) => !(isFillTargetRow(row) || isChildFillTargetRow(row)),
        getClipboardText: (row) =>
          resolveMultiDynamicSelection(row, meta, meta._id)
            .value.map((option) => option.keyName ?? '')
            .join(', '),
        valueGetter: (params) => fillOverride(params.data, field) ?? multiDynamicFillValue(params.data, meta._id),
        valueSetter: (params) => {
          (params.data as Record<string, unknown>)[field] = params.newValue;
          return true;
        },
      });
    });
    cols.push(
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.PLACE_HOLDER_QUANTITY'),
        field: 'placeholderQuantity',
        cellRenderer: PlaceholderQtyCell,
        cellProps: { getCtx },
        width: 200,
        sortable: false,
        pinned: 'right',
        ...ownCellFill('placeholderQuantity'),
      },
      {
        headerName: t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.ACTUAL_QUANTITY'),
        field: 'actualQuantity',
        cellRenderer: ActualQtyCell,
        cellProps: { getCtx },
        width: 150,
        sortable: false,
        pinned: 'right',
      },
    );
    return cols;
  }, [t, subBrandAccess, dynamicMeta, multiDynamicMeta, getCtx, getChildCtx, isFillTargetRow, isChildFillTargetRow]);

  const getRowId = useCallback((row: PMGridRow): BombiixRowId => String(row.__gridId ?? row._id ?? ''), []);

  const getRowClass = useCallback((row: PMGridRow | undefined) => (row?.__isChild ? 'phx-child-row' : ''), []);

  const onDeleteRow = useCallback(
    (rowId: BombiixRowId) => {
      const index = resolveIndexById(rowId);
      if (index < 0) return;
      const row = (placeholderStateRef.current.placeholderManagement as PMGridRow[])[index];
      handleDelete(row?._id ? (row._id as string) : index);
    },
    [resolveIndexById, handleDelete],
  );

  const canDeleteRow = useCallback(
    (row: PMGridRow) => Boolean(placeholderDeleteAccess) && !individualDetail?.isArchived && !row?.__isChild,
    [placeholderDeleteAccess, individualDetail],
  );

  const renderRowActionExtras = useCallback(
    (rowParams: BombiixCellRendererParams<PMGridRow>) => {
      const row = rowParams?.data;
      if (row?.__isChild) return ChildActions(row, childCtxRef.current);
      const canAdd = Boolean(placeholderAddEditAccess) && !individualDetail?.isArchived;
      return (
        <>
          {canAdd && (
            <button
              type="button"
              className="bombiix-icon-container bombiix-icon-container--blue"
              title="Duplicate row"
              aria-label="Duplicate row"
              onClick={() => {
                const idx = resolveIndexByRow(row as PMGridRow);
                if (idx >= 0) addRow(idx);
              }}
            >
              <AddIcon size={24} />
            </button>
          )}
          {StatusExtras(row?.placeholderStatus)}
        </>
      );
    },
    [placeholderAddEditAccess, individualDetail, resolveIndexByRow, addRow],
  );

  if (loader) return <Loader />;

  return (
    <Container className="h-100" fluid>
      <div className="fixed-col-container">
        <BombiixAGGrid<PMGridRow>
          columnDefs={columnDefs}
          rowData={gridRowData}
          getRowId={getRowId}
          getRowClass={getRowClass}
          onRowChange={handleRowChange}
          enableFillHandle
          fillHandleDirection="y"
          onFillCells={handleFillCells}
          {...(placeholderDeleteAccess ? { onDeleteRow } : {})}
          canDeleteRow={canDeleteRow}
          renderRowActionExtras={renderRowActionExtras}
          rowActionsClassName="pm-row-actions"
          currencySymbol={currencySymbol}
          onLoadMore={handlePageNextInScroll}
          hasMore={hasMore}
          loadingMore={isPageLoader}
          height="calc(100vh - 260px)"
          fullWidthHorizontalScroll
          fullWidthHorizontalScrollEdgeRight
        />
      </div>
      {showConfirmation && (
        <ConfirmationPopupTyped
          onOpen={showConfirmation}
          onConfirm={() => deleteConfirmed()}
          title={t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CONFIRM_DELETE_TITLE')}
          message={t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CONFIRM_DELETE_MESSAGE')}
          toggle={() => setShowConfirmation(!showConfirmation)}
          loading={isLoadingConf}
        />
      )}
      {showUpdatePopup?.show && (
        <ConfirmationPopupTyped
          onOpen={showUpdatePopup?.show}
          onConfirm={() => updatePlaceholderQty()}
          title={t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CONFIRM_UPDATE_QTY_TITLE')}
          message={t('PLACEHOLDER_MANAGEMENT.PLACEMENT_MANAGEMENT_LIST.CONFIRM_UPDATE_QTY_MESSAGE')}
          toggle={() => setShowUpdatePopup((prev) => ({ ...prev, show: false, value: null }))}
          loading={isLoadingConf}
        />
      )}
      {childModal.isReplaceModalOpen && (
        <ReplacePlaceholderModal isOpen={childModal.isReplaceModalOpen} handleClose={handleChildReplaceClose} devOrderId={childModal.devOrderId as string} />
      )}
    </Container>
  );
}

export default PlaceholderGrid;

/**
 * The options a placeholder row's dropdown offers, the same list its cell renders.
 * @param row - the placeholder row
 * @param field - the dropdown column
 * @param ctx - the grid's option lists
 * @returns the options, empty for a column that is not a dropdown
 */
function masterOptionsFor(row: PMGridRow, field: string, ctx: CellCtx): IdNameOption[] {
  const sources: Record<string, () => IdNameOption[]> = {
    productTypeId: () => ctx.productTypeOptions || [],
    catOneId: () => cat1OptionsFor(row, ctx) as unknown as IdNameOption[],
    catTwoId: () => cat2OptionsFor(row, ctx) as unknown as IdNameOption[],
    catThreeId: () => cat3OptionsFor(row, ctx),
    catFourId: () => cat4OptionsFor(row, ctx),
    brandId: () => ctx.brandOptions || [],
    subBrandId: () => (ctx.subBrandOptions || []).filter((option) => option.brandId === idOf(row.brandId) && option.isActive === true),
    seasonId: () => ctx.seasonOptions || [],
    month: () => ctx.monthOptions || [],
    year: () => ctx.yearOptions || [],
  };
  return sources[field]?.() ?? [];
}

/**
 * The text a Placeholder cell shows, which is what a copy puts on the clipboard: an option's name,
 * the label behind a stored id, or a stored name or number as it is. A product sub-row's Product
 * Type column shows the product's supplier type.
 * @param row - the grid row
 * @param field - the column copied
 * @param ctx - the grid's option lists
 * @returns the cell's text
 */
function fillCellText(row: PMGridRow, field: string, ctx: CellCtx): string {
  const record = row as Record<string, unknown>;
  const value = record[row.__isChild && field === 'productTypeId' ? 'supplierType' : field];
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') return String((value as IdNameOption).name ?? '');
  const options = field === 'subBrandId' ? ctx.subBrandOptions || [] : masterOptionsFor(row, field, ctx);
  const label = options.find((option) => idOf(option) === String(value))?.name ?? record[field.replace(/Id$/, 'Name')];
  return String(label ?? value);
}

/**
 * Applies a fill or paste to a placeholder row's custom attribute or Multi Custom Attribute column.
 * A Multi Custom Attribute column takes the dragged option ids, or pasted labels that all match its
 * options, and replaces the row's selection for that column.
 * @param row - the placeholder row, mutated in place
 * @param field - the column, `dyn:<id>` or `mdyn:<id>`
 * @param newValue - the dropped value
 * @param multiDynamicMeta - the Multi Custom Attribute labels and their options
 * @returns true when the row changed
 */
function applyMasterAttributeFill(row: PMGridRow, field: string, newValue: unknown, multiDynamicMeta: MultiDynamicMeta[]): boolean {
  if (field.startsWith('dyn:')) return applyDynamicFill(row, field.slice(4), newValue);
  if (!field.startsWith('mdyn:')) return false;
  const colId = field.slice(5);
  const meta = multiDynamicMeta.find((m) => m._id === colId);
  if (!meta || !isMultiDynamicEnabledForRow(meta, idOf(row?.productTypeId))) return false;
  const ids = multiDynamicIdsOf(newValue, meta.options);
  if (!ids) return false;
  row.multiCustomAttribute = upsertMultiDynamicEntry((row.multiCustomAttribute || []) as PlaceholderMultiDynamicColumn[], colId, ids);
  return true;
}

/**
 * The value a dropdown or radio custom attribute takes from a fill or paste: a dragged option the
 * attribute offers, or the option whose label was pasted. An empty value clears the attribute.
 * @param value - the dropped value
 * @param options - the attribute's options
 * @returns the value to store, or undefined when the attribute does not offer it
 */
function choiceFillValue(value: unknown, options: Array<{ _id?: string; keyName?: string }>): IdNameOption | null | undefined {
  if (!value) return (value ?? null) as IdNameOption | null;
  if (typeof value === 'object') {
    const id = idOf(value as { _id?: string });
    return !options.length || options.some((option) => option?._id === id) ? (value as IdNameOption) : undefined;
  }
  return matchOption(options, value) as IdNameOption | undefined;
}
